import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AssociationEnum,
  CiscoFields,
  CiscoMigrationSite,
  CiscoNumberReportEndUser,
  CiscoPbxExport,
  CiscoPhoneNumberReport,
  CucmE164TransformationRule,
  DeviceCategory,
  IgnoredEntry,
  JobState,
  JobStatus,
  LdapUserSyncAttribute,
  LineDevice,
  LineUsageTypeV2,
  LookupStatus,
  Microsoft365UserEntry,
  MicrosoftBulkJob,
  MicrosoftDialPlanFieldConfig,
  MigrationStatusV2,
  NumberReportEntry,
  NumberStatusV2,
  SiteResolution,
  SiteSource,
  SiteStatus,
  TeamsNumberSource,
  TeamsUserSource,
  ToTeamsNumberV2,
  UserStatusV2,
  ZpmPrivilege,
} from '../../../shared/models/generated/ziroModels';
import { ButtonStyles } from '../../../button/button.component';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { combineLatest, forkJoin, Observable, of, Subscription, take, timer } from 'rxjs';
import {
  DatatableBadge,
  DatatableColumn,
  DatatableOnRowFieldChange,
  DatatableRow,
} from '../../../shared/tables/datatable/datatable.models';
import { DatatableFilterService } from '../../../shared/tables/datatable/datatable-filter.service';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { ZiroIcons, ZiroStackedIcons } from '../../../shared/models/ziro-icons.enum';
import { FilterSelectOption } from '../../../shared/tables/datatable/filter-select/filter-select.component';
import { TranslateService } from '@ngx-translate/core';
import {
  DatatableFieldTypes,
  DatatableSelectField,
  DatatableTextField,
} from '../../../shared/tables/datatable/datatable-field.models';
import { ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { ZiroModalDetailedModalOptions } from '../../../modals/detailed-modal/detailed-modal.component';
import { MigrationsBulkJobMigrationModalComponent } from '../../../modals/bulk-job-migration-modal/migrations-bulk-job-migration-modal.component';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { MicrosoftDialPlansFieldConfigContext } from '../../../shared/contexts/microsoft-dial-plans-field-config.context';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ToastService } from '../../../shared/services/toast.service';
import moment from 'moment/moment';
import { MigrationsBulkJobMigrationModalService } from '../../../modals/bulk-job-migration-modal/migrations-bulk-job-migration-modal.service';
import { cloneDeep } from 'lodash';
import { NumberTranslationRulesModalComponent } from '../../../modals/number-translation-rules-modal/number-translation-rules-modal.component';
import {
  COLOR_PALETTE,
  migrationsNumberSortFn,
  MigrationsService,
  UiLineType,
  UiSuggestedCallingProduct,
} from '../../services/migrations.service';
import { ZpmUserPhotoContext } from '../../../shared/contexts/zpm-user-photo.context';
import { DatatableMultiSelectFilterComponent } from '../../../shared/tables/datatable/datatable-filters/datatable-multi-select-filter/datatable-multi-select-filter.component';
import { MigrationsSuggestedCallingProductPipe } from '../../services/migrations-suggested-calling-product.pipe';
import { DatatableComponent } from '../../../shared/tables/datatable/datatable.component';
import { MicrosoftBulkProvisioningPollingService } from '../../../shared/contexts/microsoft-bulk-provisioning-polling.service';
import { delayWhen, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { WidgetFilter } from '../migrations-cisco-dashboard/migrations-cisco-dashboard.component';
import { Microsoft365UserResource } from '../../../helpdesk/shared/resources/microsoft-365-user.resource';
import {
  MicrosoftCucmE164TransformationRulesContext,
  NormalizedTranslationRule,
} from '../../../shared/contexts/microsoft-cucm-e164-transformation-rules.context';
import { MigrationsAssessmentsContext } from '../../services/migrations-assessments.context';
import { MigrationsCucmHuntListsContext } from '../../services/cisco/migrations-cucm-hunt-lists.context';
import { MigrationsCucmNumberReportContext } from '../../services/cisco/migrations-cucm-number-report.context';
import { AuthenticationContext } from '../../../shared/contexts/authentication.context';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import { MigrationsCiscoSiteManagementContext } from '../../site-migration/migrations-cisco-site-management/migrations-cisco-site-management.context';

function badgeColSortFn(sortedBadgeStringsA: string, sortedBadgeStringsB: string): number {
  if (!sortedBadgeStringsA) {
    return -1;
  }
  if (!sortedBadgeStringsB) {
    return 1;
  }
  return sortedBadgeStringsB.localeCompare(sortedBadgeStringsA);
}

interface AssociatedDeviceFieldParams {
  associatedDeviceInfo: { [key: string]: LineDevice[] };
  sharedDevices: LineDevice[];
  allSameSharedValue: boolean;
  value: string;
  tooltip: string;
}

type FilterKeys =
  | 'migrationStatus'
  | 'lineType'
  | 'deviceType'
  | 'huntListMembership'
  | 'dnCallingSearchSpace'
  | 'routePartition'
  | 'devicePool'
  | 'deviceCss'
  | 'suggestedCallingProduct'
  | 'site';

export interface CucmDnReportRow extends DatatableRow {
  id?: string;
  migrationStatus?: string;
  isMigrationStatusPending?: boolean;
  rowId?: string;
  directoryNumber?: string;

  // AI Insights
  lineType?: string;
  deviceType?: string;
  suggestedCallingProduct?: string;
  cucmEndUser?: string;
  endUsers?: CiscoNumberReportEndUser[];
  huntListMembership?: string;
  site?: string;
  // Total Call Record Utilization
  totalIncomingCalls?: number;
  totalOutgoingCalls?: number;
  totalIncomingMinutes?: number;
  totalOutgoingMinutes?: number;
  // CUCM End User
  displayName?: string;
  userStatus?: string;
  firstName?: string;
  lastName?: string;
  mailId?: string;
  ms365User?: string;
  businessPhoneNumber?: string;
  // MS365 User
  ldapUserSyncAttribute?: string;
  ldapDirectoryName?: string;
  // Directory Number
  description?: string;
  dnAlertingName?: string;
  alertingNameAscii?: string;
  callingSearchSpace?: string;
  routePartition?: string;
  // Line 1 Associated Devices
  associatedLine1Name?: string[];
  associatedSecondaryLineName?: string[];
  associatedDeviceCss?: string;
  devicePool?: string;
  externalMask?: string;
  lineDisplay?: string;
  // Move to Microsoft Teams
  teamsUser?: string;
  isTeamsUserMigrated?: boolean;
  teamsNumber?: string;
  teamsNumberObject?: ToTeamsNumberV2;
  // Suggested Calling Product
  diffInDays?: number;
  // Monthly PSTN Call Record Utilization
  monthlyPstnIncomingCallsOutgoingMinutes?: string;
  monthlyPstnInboundCalls?: number;
  monthlyPstnOutgoingCalls?: number;
  monthlyPstnIncomingCallsDurationInMinutes?: number;
  monthlyPstnOutgoingCallsDurationInMinutes?: number;
}

@Component({
  selector: 'ziro-migrations-cisco-directory-numbers-report',
  templateUrl: 'migrations-cisco-directory-numbers-report.component.html',
  styleUrls: ['../../migrations.component.scss'],
  providers: [ZiroModalService, MigrationsBulkJobMigrationModalService, MigrationsCiscoSiteManagementContext],
  standalone: false,
})
export class MigrationsCiscoDirectoryNumbersReportComponent implements OnInit, OnDestroy {
  @ViewChild('datatable') datatableComponent: DatatableComponent<CucmDnReportRow>;
  @ViewChild('migrationStatusFilter') migrationStatusFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('lineTypeFilter') lineTypeFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('deviceTypeFilter') deviceTypeFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('huntListMembershipFilter') huntListMembershipFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('dnCallingSearchSpaceFilter') dnCallingSearchSpaceFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('routePartitionFilter') routePartitionFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('devicePoolFilter') devicePoolFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('deviceCssFilter') deviceCssFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('suggestedCallingProductFilter') suggestedCallingProductFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('siteFilter') siteFilter: DatatableMultiSelectFilterComponent;
  @ViewChild('deviceUsageTypeCell', { static: true }) deviceUsageTypeCellTemplate: TemplateRef<any>;

  readonly MAX_ASSOCIATED_DEVICE_LENGTH = 30;
  readonly MAX_ASSOCIATED_DEVICE_BADGE_LENGTH = this.MAX_ASSOCIATED_DEVICE_LENGTH - 10;

  private static DEFAULT_HIDDEN_COLUMNS: string[] = [
    'tkey;migrations.cisco.directory_numbers_report.table.upn.label',
    'tkey;migrations.cisco.directory_numbers_report.table.display_name.label',
    'tkey;migrations.cisco.directory_numbers_report.table.user_status.label',
    'tkey;migrations.cisco.directory_numbers_report.table.ldap_sync_attribute.label',
    'tkey;migrations.cisco.directory_numbers_report.table.ldap_directory_name.label',
    'tkey;migrations.cisco.directory_numbers_report.table.first_name.label',
    'tkey;migrations.cisco.directory_numbers_report.table.last_name.label',
    'tkey;migrations.cisco.directory_numbers_report.table.mail_id.label',
    'tkey;migrations.cisco.directory_numbers_report.table.business_phone_number.label',
    'tkey;migrations.cisco.directory_numbers_report.table.total_incoming_calls.label',
    'tkey;migrations.cisco.directory_numbers_report.table.total_outgoing_calls.label',
    'tkey;migrations.cisco.directory_numbers_report.table.total_incoming_minutes.label',
    'tkey;migrations.cisco.directory_numbers_report.table.total_outgoing_minutes.label',
    'tkey;migrations.cisco.directory_numbers_report.table.description.label',
    'tkey;migrations.cisco.directory_numbers_report.table.alerting_name_ascii.label',
    'tkey;migrations.cisco.directory_numbers_report.table.calling_search_space.label',
    'tkey;migrations.cisco.directory_numbers_report.table.route_partition.label',
    'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.line_display.label',
    'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.external_mask.label',
    'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.device_pool.label',
    'tkey;migrations.cisco.directory_numbers_report.table.line_1_associated_devices.name.label',
    'tkey;migrations.cisco.directory_numbers_report.table.secondary_line_associated_devices.name.label',
    'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.css.label',
    'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_calls_outgoing_minutes.column_label',
    'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_calls.label',
    'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_minutes.label',
    'tkey;migrations.cisco.directory_numbers_report.table.monthly_outgoing_calls.label',
    'tkey;migrations.cisco.directory_numbers_report.table.monthly_outgoing_minutes.label',
  ];
  private static INCOMING_OUTGOING_SEPARATOR = ' / ';

  private _migrationsService = inject(MigrationsService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _bottomNavService = inject(BottomNavService);
  private _migrationsAssessmentsContext = inject(MigrationsAssessmentsContext);
  private _datatableFilterService = inject(DatatableFilterService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _translateService = inject(TranslateService);
  private _ziroModalService = inject(ZiroModalService);
  private _microsoftDialPlansFieldConfigContext = inject(MicrosoftDialPlansFieldConfigContext);
  private _microsoftBulkProvisioningPollingService = inject(MicrosoftBulkProvisioningPollingService);
  private _toastService = inject(ToastService);
  private _migrationsBulkJobMigrationModalService = inject(MigrationsBulkJobMigrationModalService);
  private _cucmE164TransformationRulesContext = inject(MicrosoftCucmE164TransformationRulesContext);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _zpmUserPhotoContext = inject(ZpmUserPhotoContext);
  private _migrationCucmHuntListsContext = inject(MigrationsCucmHuntListsContext);
  private _migrationsCucmNumberReportContext = inject(MigrationsCucmNumberReportContext);
  private _suggestedCallingProductPipe = inject(MigrationsSuggestedCallingProductPipe);
  private _microsoft365UserResource = inject(Microsoft365UserResource);
  private _authenticationContext = inject(AuthenticationContext);
  private _migrationsCiscoSiteManagementContext = inject(MigrationsCiscoSiteManagementContext);

  utilizationSummary: string;
  cdrDiffDays: number;
  buttonStyles = ButtonStyles;
  ZiroStackedIcons = ZiroStackedIcons;
  ZiroIcons = ZiroIcons;
  isLoading = true;
  isRefreshing = false;
  isManualRefresh = false;
  isAutomationAllowed = false;
  tableDataAutomation = 'cisco-directory-numbers-report-table';
  initialGroupedColumnFilterOptions: FilterSelectOption[];

  migrationStatusOptionsWithCounts: FilterSelectOption[] = [];
  lineTypeOptionsWithCounts: FilterSelectOption[] = [];
  dnCallingSearchSpaceOptionsWithCounts: FilterSelectOption[] = [];
  routePartitionOptionsWithCounts: FilterSelectOption[] = [];
  devicePoolOptionsWithCounts: FilterSelectOption[] = [];
  deviceCssOptionsWithCounts: FilterSelectOption[] = [];
  suggestedCallingProductOptionsWithCounts: FilterSelectOption[] = [];
  countedHuntListMembershipFilterOptions: FilterSelectOption[] = [];
  deviceTypeOptionsWithCounts: FilterSelectOption[] = [];
  siteOptionsWithCounts: FilterSelectOption[] = [];
  showCustomAlert = false;
  data: CucmDnReportRow[] = [];
  manualEntryIcon = this._getIconHtml(ZiroIcons.MANUAL_ENTRY);
  translationsIcon = this._getIconHtml(ZiroIcons.NUMBER_TRANSLATIONS);

  columns: DatatableColumn<CucmDnReportRow>[] = [
    {
      name: 'migrationStatus',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.migration_status.label',
      subLabel: '&nbsp;',
      columnCharacterWidth: 12,
      pipe: {
        transform: (value: string, isPending: boolean): string => {
          if (!value) {
            return '';
          }

          if (isPending) {
            return `
              <span class="alert alert-inline alert-info">
                <i class="${ZiroIcons.LOADING} text-warning"></i>
                <span>${this._translateService.instant('tkey;migrations.number_report.table.migration_status.pending.label')}</span>
              </span>
            `;
          } else {
            const iconColor = Array.from(MigrationsService.MIGRATION_STATUS_ROW_MAP.values()).find(
              (val) => val.label === value,
            ).iconColor;

            if (value === MigrationsService.MIGRATION_STATUS_ROW_MAP.get(MigrationStatusV2.STAGED).label) {
              return `
              <span class="alert alert-inline alert-info">
                <i class="${ZiroIcons.CIRCLE_OUTLINE} fa-2xs" style="color: ${COLOR_PALETTE.msTeamsPurple};"></i>
                <span>${value}</span>
              </span>
            `;
            }
            return `
              <span class="alert alert-inline alert-info">
                <i class="${ZiroIcons.CIRCLE} fa-2xs" style="color: ${iconColor};"></i>
                <span>${value}</span>
              </span>
            `;
          }
        },
      },
      pipeArgs: ['isMigrationStatusPending'],
    },
    {
      name: 'rowId',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
      hideColumn: true,
      sortFn: (a: string, b: string): number => migrationsNumberSortFn(a, b),
    },
    {
      name: 'directoryNumber',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
      subLabel: '&nbsp;',
      pipe: {
        transform: (value: string): string => {
          return `<strong>${this._telephoneNumberFilter.transform(value)}</strong>`;
        },
      },
      columnCharacterWidth: 20,
      sortFn: (a: string, b: string): number => migrationsNumberSortFn(a, b),
    },
    {
      name: 'lineType',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.usage_type.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.sub_label.ai_insights.label',
      columnCharacterWidth: 20,
    },
    {
      name: 'deviceType',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.device_usage_type.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.sub_label.ai_insights.label',
      columnCharacterWidth: 20,
      cellTemplate: () => this.deviceUsageTypeCellTemplate,
    },
    {
      name: 'suggestedCallingProduct',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.suggested_calling_product.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.sub_label.ai_insights.label',
      columnCharacterWidth: 20,
      pipe: this._suggestedCallingProductPipe,
      pipeArgs: 'row',
    },
    {
      name: 'cucmEndUser',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.sub_label.ai_insights.label',
      columnCharacterWidth: 22,
    },
    {
      name: 'huntListMembership',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.hunt_list_membership.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.sub_label.ai_insights.label',
      columnCharacterWidth: 30,
      sortFn: badgeColSortFn,
    },
    {
      name: 'site',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.site.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.sub_label.ai_insights.label',
      columnCharacterWidth: 20,
    },
    {
      name: 'displayName',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.display_name.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    {
      name: 'userStatus',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.user_status.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
      columnCharacterWidth: 22,
      pipe: {
        transform: (value: string, cucmEndUser: string): string => {
          if (!cucmEndUser) {
            return '';
          }

          const badgeLdap = `
            <span class="alert alert-inline alert-info">
              <i class="${ZiroIcons.USER} text-success"></i>
              <span>${this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.table.user_status.ldap.label')}</span>
            </span>
          `;
          const badgeLocal = `
            <span class="alert alert-inline alert-info">
              <i class="${ZiroIcons.USER}"></i>
              <span>${this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.table.user_status.local.label')}</span>
            </span>
          `;

          switch (value) {
            case LdapUserSyncAttribute.SAM_ACCOUNT_NAME: {
              return badgeLdap;
            }
            case LdapUserSyncAttribute.LOCAL_USER: {
              return badgeLocal;
            }
            default: {
              return '';
            }
          }
        },
      },
      pipeArgs: ['cucmEndUser'],
    },
    {
      name: 'ldapUserSyncAttribute',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.ldap_sync_attribute.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
      columnCharacterWidth: 25,
    },
    {
      name: 'ldapDirectoryName',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.ldap_directory_name.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    {
      name: 'firstName',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.first_name.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    {
      name: 'lastName',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.last_name.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    {
      name: 'mailId',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.mail_id.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    {
      name: 'ms365User',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.upn.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.ms_entra_id.label',
      columnCharacterWidth: 40,
    },
    {
      name: 'businessPhoneNumber',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.business_phone_number.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.ms_entra_id.label',
      columnCharacterWidth: 30,
    },
    {
      name: 'description',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.description.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
    },
    {
      name: 'dnAlertingName',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.alerting_name.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
      columnCharacterWidth: 25,
    },
    {
      name: 'alertingNameAscii',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.alerting_name_ascii.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
    },
    {
      name: 'callingSearchSpace',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.calling_search_space.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
    },
    {
      name: 'routePartition',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.route_partition.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
    },
    {
      name: 'totalIncomingCalls',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.total_incoming_calls.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.total_call_record_utilization.label',
      columnCharacterWidth: 30,
    },
    {
      name: 'totalOutgoingCalls',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.total_outgoing_calls.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.total_call_record_utilization.label',
      columnCharacterWidth: 30,
    },
    {
      name: 'totalIncomingMinutes',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.total_incoming_minutes.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.total_call_record_utilization.label',
      columnCharacterWidth: 30,
    },
    {
      name: 'totalOutgoingMinutes',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.total_outgoing_minutes.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.total_call_record_utilization.label',
      columnCharacterWidth: 30,
    },
    {
      name: 'monthlyPstnIncomingCallsOutgoingMinutes',
      label:
        'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_calls_outgoing_minutes.column_label',
      htmlLabel:
        'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_calls_outgoing_minutes.column_label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_call_record_utilization.label',
      columnCharacterWidth: 15,
      pipe: {
        transform: (value: string): string => {
          const split = value.split(MigrationsCiscoDirectoryNumbersReportComponent.INCOMING_OUTGOING_SEPARATOR);

          return `
            <span class="badge bg-secondary"><i class="${ZiroIcons.INCOMING_CALLS_SMALL}"></i> ${split[0]}</span>
            <span class="badge bg-secondary"><i class="${ZiroIcons.PER_MINUTE}"></i> ${split[1]}</span>
          `;
        },
      },
      sortFn: (a: string, b: string): number => {
        const aSplit = a.split(MigrationsCiscoDirectoryNumbersReportComponent.INCOMING_OUTGOING_SEPARATOR);
        const bSplit = b.split(MigrationsCiscoDirectoryNumbersReportComponent.INCOMING_OUTGOING_SEPARATOR);
        const aTotal = parseInt(aSplit[0]) + parseInt(aSplit[1]);
        const bTotal = parseInt(bSplit[0]) + parseInt(bSplit[1]);

        return aTotal - bTotal;
      },
    },
    {
      name: 'monthlyPstnInboundCalls',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_calls.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_call_record_utilization.label',
      columnCharacterWidth: 30,
    },
    {
      name: 'monthlyPstnOutgoingCalls',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_outgoing_calls.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_call_record_utilization.label',
      columnCharacterWidth: 30,
    },
    {
      name: 'monthlyPstnIncomingCallsDurationInMinutes',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_minutes.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_call_record_utilization.label',
      columnCharacterWidth: 30,
    },
    {
      name: 'monthlyPstnOutgoingCallsDurationInMinutes',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_outgoing_minutes.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_call_record_utilization.label',
      columnCharacterWidth: 30,
    },
    {
      name: 'associatedLine1Name',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.line_1_associated_devices.name.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.sub_label',
      columnCharacterWidth: this.MAX_ASSOCIATED_DEVICE_LENGTH,
      sortFn: (a: string[], b: string[]) => (a[0] ?? '').localeCompare(b[0] ?? ''),
    },
    {
      name: 'associatedSecondaryLineName',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.secondary_line_associated_devices.name.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.sub_label',
      columnCharacterWidth: this.MAX_ASSOCIATED_DEVICE_LENGTH,
      sortFn: (a: string[], b: string[]) => (a[0] ?? '').localeCompare(b[0] ?? ''),
    },
    {
      name: 'associatedDeviceCss',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.css.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.sub_label',
    },
    {
      name: 'devicePool',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.device_pool.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.sub_label',
      columnCharacterWidth: 25,
    },
    {
      name: 'externalMask',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.external_mask.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.sub_label',
      columnCharacterWidth: 25,
    },
    {
      name: 'lineDisplay',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.line_display.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.sub_label',
    },
    {
      name: 'teamsUser',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.user.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.move_to_microsoft_teams.label',
      colClass: 'move-to-teams-user-col',
      columnCharacterWidth: 35,
    },
    {
      name: 'teamsNumber',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.number.label',
      subLabel: 'tkey;migrations.cisco.directory_numbers_report.table.move_to_microsoft_teams.label',
      colClass: 'move-to-teams-number-col',
      columnCharacterWidth: 25,
      sortFn: (a: string, b: string): number => migrationsNumberSortFn(a, b),
    },
  ];

  groupedColumnFilterOptions: FilterSelectOption[] = [
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.migration_status.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.migration_status.label',
      groupBy: '&nbsp',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
      groupBy: '&nbsp',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    // AI Insights
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.usage_type.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.usage_type.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.ai_insights.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.device_usage_type.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.device_usage_type.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.ai_insights.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.site.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.site.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.ai_insights.label',
    },
    // CUCM End User
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.display_name.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.display_name.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.user_status.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.user_status.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.ldap_sync_attribute.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.ldap_sync_attribute.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.ldap_directory_name.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.ldap_directory_name.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.first_name.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.first_name.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.last_name.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.last_name.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.mail_id.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.mail_id.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
    },
    // MS365 User
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.upn.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.upn.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.ms_entra_id.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.business_phone_number.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.business_phone_number.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.ms_entra_id.label',
    },
    // DN
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.description.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.description.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.alerting_name.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.alerting_name.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.alerting_name_ascii.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.alerting_name_ascii.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.calling_search_space.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.calling_search_space.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.route_partition.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.route_partition.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.directory_number.label',
    },
    // Total Call Record Utilization
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.total_incoming_calls.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.total_incoming_calls.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.total_call_record_utilization.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.total_outgoing_calls.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.total_outgoing_calls.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.total_call_record_utilization.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.total_incoming_minutes.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.total_incoming_minutes.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.total_call_record_utilization.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.total_outgoing_minutes.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.total_outgoing_minutes.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.total_call_record_utilization.label',
    },
    // Monthly PSTN Call Record Utilization
    {
      value:
        'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_calls_outgoing_minutes.column_label',
      label:
        'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_calls_outgoing_minutes.column_label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_call_record_utilization.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_calls.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_calls.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_call_record_utilization.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_minutes.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_incoming_minutes.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_call_record_utilization.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_outgoing_calls.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_outgoing_calls.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_call_record_utilization.label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_outgoing_minutes.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_outgoing_minutes.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.monthly_call_record_utilization.label',
    },
    // Associated Devices
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.line_1_associated_devices.name.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.line_1_associated_devices.name.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.sub_label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.secondary_line_associated_devices.name.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.secondary_line_associated_devices.name.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.sub_label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.css.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.css.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.sub_label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.device_pool.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.device_pool.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.sub_label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.external_mask.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.external_mask.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.sub_label',
    },
    {
      value: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.line_display.label',
      label: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.line_display.label',
      groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.sub_label',
    },
  ];

  private _tenantId: number;
  private _assessmentUuid: string;
  private _assessment: CiscoPbxExport;
  private _dnReport: CiscoPhoneNumberReport;
  private _dialPlans: MicrosoftDialPlanFieldConfig[] = [];
  private _bulkDrafts: MicrosoftBulkJob[] = [];
  private _cucmE164TransformationRules: CucmE164TransformationRule[] = [];
  private _normalizedE164TransformationRules: NormalizedTranslationRule[];
  private _subscription = new Subscription();
  private _filter: [WidgetFilter, string];
  private _areFiltersInitialized = false;
  private _hasSubscribedToReportData = false;
  private _migrationStatusOptions: string[] = [];
  private _dnCallingSearchSpaceOptions: string[] = [];
  private _routePartitionOptions: string[] = [];
  private _devicePoolOptions: string[] = [];
  private _deviceCssOptions: string[] = [];
  private _uniqueHuntListMemberships: string[] = [];
  private _siteOptions: string[] = [];
  private _sites: CiscoMigrationSite[] = [];
  private _noneFilterLabel = this._translateService.instant(
    'tkey;migrations.cisco.directory_numbers_report.table.filter.none.label',
  );
  private _noMembershipFilterLabel = this._translateService.instant(
    'tkey;migrations.cisco.directory_numbers_report.table.filter.no_membership.label',
  );
  private _suggestedCallingProductOptions: string[] = Object.values(UiSuggestedCallingProduct).filter(
    (val) => val.length,
  );

  private readonly _lineTypeOptionsInOrder: string[] = [
    UiLineType.USER,
    UiLineType.SHARED_DEVICE,
    UiLineType.CONTACT_CENTER_AGENT,
    UiLineType.SHARED_LINE,
    UiLineType.MULTI_LINE_USER,
    UiLineType.VOICE_APP,
    UiLineType.ANALOG,
    UiLineType.INACTIVE,
    UiLineType.AUTO_ATTENDANT,
    UiLineType.ORPHAN,
    UiLineType.UNCLASSIFIED,
  ];

  private readonly _refreshButtonId = 'migrations-cisco-reports-list-refresh';
  private readonly _exportButtonId = 'migrations-cisco-dn-report-export-button';
  private readonly _migrateButtonId = 'migrations-cisco-reports-list-migrate';
  private readonly _ignoreUnignoreRowsButtonId = 'migrations-cisco-reports-list-ignore-unignore-rows-button';

  ngOnInit(): void {
    ({ tenantId: this._tenantId, id: this._assessmentUuid } = this._route.snapshot.params);
    this._filter = this._route.snapshot.paramMap.get('filter')?.split('=') as [WidgetFilter, string];

    this._initContexts();

    const translationRulesSub = this._cucmE164TransformationRulesContext.state$.subscribe((rules) => {
      this._cucmE164TransformationRules = rules;
      this._normalizedE164TransformationRules = rules
        .map((rule) => ({
          fromPrefix: rule.numberRangeToTransform.replace(/X/gi, ''),
          fromXCount: (rule.numberRangeToTransform.match(/X/gi) || []).length,
          description: rule.description,
        }))
        .sort((a, b) => a.fromXCount - b.fromXCount);
    });

    const bulkProvisioningSub = this._authenticationContext.state$
      .pipe(
        filter((currentUser) =>
          this._authenticationContext.userHasPrivilege(currentUser, ZpmPrivilege.AUTOMATION, this._tenantId),
        ),
        tap(() => this._microsoftBulkProvisioningPollingService.refresh(this._tenantId).subscribe()),
        switchMap(() =>
          combineLatest([
            this._microsoftDialPlansFieldConfigContext.state$,
            this._microsoftBulkProvisioningPollingService.state$,
          ]),
        ),
      )
      .subscribe(([dialPlans, bulkDrafts]: [MicrosoftDialPlanFieldConfig[], MicrosoftBulkJob[]]) => {
        this._dialPlans = dialPlans;
        this._bulkDrafts = bulkDrafts;
      });

    this._subscription.add(bulkProvisioningSub);
    this._subscription.add(translationRulesSub);
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: null,
      }),
    );
  }

  getDeviceUsageTypeLabel(row: CucmDnReportRow): string {
    return row.deviceType;
  }

  getDeviceUsageTypeIcon(row: CucmDnReportRow): ZiroIcons {
    return Array.from(MigrationsService.DEVICE_TYPE_ROW_MAP.values()).find((value) => value.label === row.deviceType)
      .icon;
  }

  isIconStacked(icon: string): boolean {
    return Object.keys(ZiroStackedIcons).includes(icon);
  }

  migrationStatusMatchFn = (filter: string[], row: CucmDnReportRow): boolean => {
    return this._datatableFilterService.filterMultiSelectValues(filter, row.migrationStatus);
  };

  lineTypeMatchFn = (filter: string[], row: CucmDnReportRow): boolean => {
    return this._datatableFilterService.filterMultiSelectValues(filter, row.lineType);
  };

  deviceTypeMatchFn = (filter: string[], row: CucmDnReportRow): boolean => {
    return this._datatableFilterService.filterMultiSelectValues(filter, row.deviceType);
  };

  siteMatchFn = (filter: string[], row: CucmDnReportRow): boolean => {
    return this._datatableFilterService.filterMultiSelectValues(filter, row.site);
  };

  huntListMembershipMatchFn = (filter: string[], row: CucmDnReportRow): boolean => {
    return (
      (filter.includes(this._noMembershipFilterLabel) && !row.huntListMembership) ||
      this._datatableFilterService.filterMultiSelectJoinedArrayValues(filter, row.huntListMembership)
    );
  };

  dnCallingSearchSpaceMatchFn = (filter: string[], row: CucmDnReportRow): boolean => {
    return (
      (filter.includes(this._noneFilterLabel) && !row.callingSearchSpace) ||
      this._datatableFilterService.filterMultiSelectValues(filter, row.callingSearchSpace)
    );
  };

  suggestedCallingProductMatchFn = (filter: string[], row: CucmDnReportRow): boolean => {
    return (
      (filter.includes(this._noneFilterLabel) && !row.suggestedCallingProduct) ||
      this._datatableFilterService.filterMultiSelectValues(filter, row.suggestedCallingProduct)
    );
  };

  routePartitionMatchFn = (filter: string[], row: CucmDnReportRow): boolean => {
    return (
      (filter.includes(this._noneFilterLabel) && !row.routePartition) ||
      this._datatableFilterService.filterMultiSelectValues(filter, row.routePartition)
    );
  };

  devicePoolMatchFn = (filter: string[], row: CucmDnReportRow): boolean => {
    return (
      (filter.includes(this._noneFilterLabel) && !row.devicePool) ||
      this._datatableFilterService.filterMultiSelectValues(filter, row.fields.devicePool.value.value || row.devicePool)
    );
  };

  deviceCssMatchFn = (filter: string[], row: CucmDnReportRow): boolean => {
    return (
      (filter.includes(this._noneFilterLabel) && !row.associatedDeviceCss) ||
      this._datatableFilterService.filterMultiSelectValues(
        filter,
        row.fields.associatedDeviceCss.value.value || row.associatedDeviceCss,
      )
    );
  };

  onRowFieldChange(change: DatatableOnRowFieldChange<CucmDnReportRow>): void {
    if (change.columnId === 'cucmEndUser') {
      this.data = this.data.map((row: CucmDnReportRow): CucmDnReportRow => {
        if (row.directoryNumber === change.row.directoryNumber) {
          const enduserId: string = change.newValue.value;
          const endUser = row.endUsers?.find((user) => user.userId === enduserId) ?? null;
          const cellCssClass: string = 'animate__animated animate__flipInX animate__fast table-font-sans-serif-black';

          return {
            ...row,
            ...this._getEndUserCellValues(endUser),
            fields: {
              ...row.fields,
              [change.columnId]: {
                ...row.fields[change.columnId],
                value: change.newValue,
              },
            },
            hardcodedTooltips: {
              ldapUserSyncAttribute:
                endUser?.ldapUserSyncAttribute === LdapUserSyncAttribute.LOCAL_USER
                  ? this._translateService.instant(
                      'tkey;migrations.cisco.directory_numbers_report.table.ldap_sync_attribute.local.tooltip',
                    )
                  : '',
            },
            hardcodedDisableTooltip: {
              ldapUserSyncAttribute: endUser?.ldapUserSyncAttribute !== LdapUserSyncAttribute.LOCAL_USER,
            },
            cssClass: 'bg-primary-light transition-background-color',
            cellCssClass: {
              ...row.cellCssClass,
              displayName: cellCssClass,
              userStatus: cellCssClass,
              ldapUserSyncAttribute: cellCssClass,
              ldapDirectoryName: cellCssClass,
              firstName: cellCssClass,
              lastName: cellCssClass,
              mailId: cellCssClass,
              ms365User: cellCssClass,
              businessPhoneNumber: cellCssClass,
            },
            isCellDisabled: {
              ...row.isCellDisabled,
              ms365User: endUser?.microsoft365User?.lookupStatus !== LookupStatus.FOUND,
            },
          };
        }

        return {
          ...row,
          cssClass: '',
          cellCssClass: {
            ...row.cellCssClass,
            displayName: '',
            userStatus: '',
            ldapUserSyncAttribute: '',
            ldapDirectoryName: '',
            firstName: '',
            lastName: '',
            mailId: '',
            ms365User: '',
            businessPhoneNumber: '',
          },
        };
      });

      timer(750).subscribe(() => {
        this._removeRowUpdatedStyles();
      });
    }
  }

  onRowSelected(): void {
    this._initBottomNav();
  }

  openNumberTranslationRulesModal(): void {
    const options = {
      size: 'lg',
      modalViewProperties: {
        title: 'tkey;migrations.cisco.directory_numbers_report.manage_number_translation_rules.modal.title',
        e164TransformationRules: this._cucmE164TransformationRules,
        directoryNumbers: this._dnReport.entries.map((entry) => entry.pbxFields.directoryNumber),
        tenantId: this._tenantId,
        reportId: this._assessmentUuid,
        submitButtonLabel:
          'tkey;migrations.cisco.directory_numbers_report.manage_number_translation_rules.modal.submit',
      },
      bodyClass: NumberTranslationRulesModalComponent,
      submitFn: () => this._migrationsCucmNumberReportContext.softRefresh(),
    };
    this._ziroModalService.openDetailedModal(() => options.modalViewProperties, options).subscribe();
  }

  onFilterInitialized(): void {
    if (!this._filter) {
      return;
    }
    setTimeout(() => {
      const type: WidgetFilter = this._filter[0];
      const filter: string = decodeURIComponent(this._filter[1]);

      if (
        !this.lineTypeFilter?.selectedItems.length &&
        type === 'lineType' &&
        Object.values(UiLineType).includes(filter)
      ) {
        this.lineTypeFilter?.onFilterChange(filter);
        this._changeDetectorRef.detectChanges();
      } else if (
        !this.suggestedCallingProductFilter?.selectedItems.length &&
        type === 'callingProduct' &&
        Object.values(UiSuggestedCallingProduct).includes(filter as UiSuggestedCallingProduct)
      ) {
        this.suggestedCallingProductFilter?.onFilterChange(filter);
        this._changeDetectorRef.detectChanges();
      } else if (!this.deviceTypeFilter?.selectedItems.length && type === 'deviceType') {
        this.deviceTypeFilter?.onFilterChange(filter);
        this._changeDetectorRef.detectChanges();
      } else if (!this.migrationStatusFilter?.selectedItems.length && type === 'migrationStatus') {
        this.migrationStatusFilter?.onFilterChange(filter);
        this._changeDetectorRef.detectChanges();
      }
    });
  }

  onExportDNReportClicked(): void {
    this._migrationsService.dispatchButtonState(this._refreshButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: true,
      },
    });

    this._migrationsService.dispatchButtonState(this._exportButtonId, {
      pending: true,
      buttonDisableState: {
        disabled: true,
      },
    });

    this._migrationsService.dispatchButtonState(this._migrateButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: true,
      },
    });

    this._migrationsService.dispatchButtonState(this._ignoreUnignoreRowsButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: true,
      },
    });

    this._subscription.add(
      this._migrationsCucmNumberReportContext
        .exportDirectoryNumbersReport()
        .pipe(
          take(1),
          finalize(() => this._resetButtonStates()),
        )
        .subscribe({
          next: () =>
            this._toastService.push(
              ToastTypes.SUCCESS,
              ZiroIcons.EXPORT,
              'tkey;migrations.cisco.directory_numbers_report.toast.xlsx_export.title',
              'tkey;migrations.cisco.directory_numbers_report.toast.xlsx_export.message',
            ),
        }),
    );
  }

  private _initFilterOptionLists(): void {
    const noneFilterOption = {
      value: '',
      label: this._noneFilterLabel,
      count: 0,
    };

    const noMembershipHuntListOption = {
      value: '',
      label: this._noMembershipFilterLabel,
      count: 0,
    };

    this.lineTypeOptionsWithCounts = this._lineTypeOptionsInOrder.map((option) => ({
      value: option,
      label: option,
      count: 0,
    }));
    this.deviceTypeOptionsWithCounts = [
      { value: 'No Device', label: 'No Device', count: 0 },
      ...Array.from(MigrationsService.DEVICE_TYPE_ROW_MAP.values())
        .filter((deviceTypeInfo) => deviceTypeInfo.label !== 'No Device')
        .map((deviceTypeInfo) => ({
          value: deviceTypeInfo.label,
          label: deviceTypeInfo.label,
          count: 0,
        })),
    ];

    this.migrationStatusOptionsWithCounts = this._toFilterOptions(this._migrationStatusOptions);
    this.devicePoolOptionsWithCounts = this._toFilterOptions(this._devicePoolOptions);
    this.suggestedCallingProductOptionsWithCounts = this._toFilterOptions(this._suggestedCallingProductOptions);
    this.routePartitionOptionsWithCounts = this._toFilterOptions(this._routePartitionOptions);

    this.deviceCssOptionsWithCounts = [noneFilterOption, ...this._toFilterOptions(this._deviceCssOptions)];
    this.dnCallingSearchSpaceOptionsWithCounts = [
      noneFilterOption,
      ...this._toFilterOptions(this._dnCallingSearchSpaceOptions),
    ];
    this.countedHuntListMembershipFilterOptions = [
      noMembershipHuntListOption,
      ...this._toFilterOptions(this._uniqueHuntListMemberships),
    ];
  }

  private _toFilterOptions(values: string[]): FilterSelectOption[] {
    return (values || []).sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v, count: 0 }));
  }

  private _getAssociatedDeviceBadgeIcon(device: LineDevice): ZiroIcons {
    switch (device.deviceCategory) {
      case DeviceCategory.SIP_GATEWAY_SUPPORTED_PHONE:
      case DeviceCategory.UNSUPPORTED_SIP_GATEWAY_PHONE:
        return ZiroIcons.DESKPHONE_SMALL;
      case DeviceCategory.EXTENSION_MOBILITY:
        return ZiroIcons.EXTENSION_MOBILITY_SMALL;
      case DeviceCategory.ANALOG_FAX:
        return ZiroIcons.FAX_LINE_SMALL;
      case DeviceCategory.ANALOG_MODEM:
        return ZiroIcons.MODEM_LINE_SMALL;
      case DeviceCategory.ANALOG_OTHER:
        return ZiroIcons.ANALOG_LINE_SMALL;
      case DeviceCategory.CTI_PORT:
        return ZiroIcons.CTI_PORT_LINE_SMALL;
      default:
        break;
    }

    switch (device.model) {
      case 'Cisco Unified Client Services Framework':
        return ZiroIcons.SOFTPHONE_SMALL;
      case 'Cisco Dual Mode for iPhone':
        return ZiroIcons.IPHONE_SMALL;
      case 'Cisco Dual Mode for Android':
        return ZiroIcons.ANDROID_SMALL;
      case 'Cisco Jabber for Tablet':
        return ZiroIcons.TABLET_SMALL;
      case 'Cisco IP Communicator':
        return ZiroIcons.CIPC_SOLID;
      case 'Cisco Spark Remote Device':
        return ZiroIcons.PHONE_OTHER_OUTLINE;
      default:
        break;
    }
  }

  private _buildAssociatedDeviceBadges(devices: LineDevice[], tooltipKey: string): DatatableBadge<CucmDnReportRow>[] {
    if (!devices.length) {
      return [];
    }

    const totalChars = devices.map((device) => device.name).reduce((sum, name) => sum + name.length, 0);

    if (totalChars <= this.MAX_ASSOCIATED_DEVICE_BADGE_LENGTH) {
      return devices.map((device) => ({ label: device.name, icon: this._getAssociatedDeviceBadgeIcon(device) }));
    }

    const moreTkey: string = this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.more_devices.label',
    );
    const badges: DatatableBadge<CucmDnReportRow>[] = [];
    let remainingNames = devices.length;
    let totalCharsInBadgesSoFar = 0;

    for (let i = 0; i < devices.length; i++) {
      const nameLength = devices[i].name.length;

      if (
        moreTkey.length + nameLength + remainingNames.toString().length + totalCharsInBadgesSoFar <=
        this.MAX_ASSOCIATED_DEVICE_BADGE_LENGTH
      ) {
        badges.push({ label: devices[i].name, icon: this._getAssociatedDeviceBadgeIcon(devices[i]) });
        totalCharsInBadgesSoFar += nameLength;
        remainingNames--;
      } else {
        break;
      }
    }

    const moreLabel = remainingNames + moreTkey;
    const namesTooLong = devices.length > 1 && !badges.length;

    if (badges.length) {
      badges.push({
        label: moreLabel,
        hardcodedTooltip: this._buildAssociatedDeviceNamesTooltip(
          devices.slice(devices.length - remainingNames),
          tooltipKey,
        ),
      });
    } else if (namesTooLong) {
      badges.push({
        label: this._translateService.instant(
          'tkey;migrations.number_report.table.associated_devices.all_devices.label',
          {
            count: devices.length,
          },
        ),
        hardcodedTooltip: this._buildAssociatedDeviceNamesTooltip(devices, tooltipKey),
      });
    } else {
      badges.push({
        label: devices[0].name,
        showTooltipOnTruncate: true,
        icon: this._getAssociatedDeviceBadgeIcon(devices[0]),
      });
    }

    return badges;
  }

  private _buildAssociatedDeviceNamesTooltip(devices: LineDevice[], tkey: string): string {
    const translatedTkey = this._translateService.instant(tkey);
    const formattedNames = devices
      .map((device) => `<li><i class="me-1 ${this._getAssociatedDeviceBadgeIcon(device)}"></i>${device.name}</li>`)
      .join('');

    return `
    <div>
      <div>${translatedTkey}</div>
      <ul class="text-start mb-0">
      ${formattedNames}
      </ul>
    </div>`;
  }

  private _setUtilizationSummary(): void {
    if (!this._dnReport) {
      return;
    }

    const oldestMoment = moment(new Date(this._dnReport.oldestCdrTimestamp));
    const newestMoment = moment(new Date(this._dnReport.mostRecentCdrTimestamp));
    this.cdrDiffDays = newestMoment.diff(oldestMoment, 'days');
    const formattedOldestDate = moment(oldestMoment).format('MMMM Do, YYYY');
    const formattedNewestDate = moment(newestMoment).format('MMMM Do, YYYY');

    if (!this._dnReport.totalNumberOfCdrs) {
      this.utilizationSummary = this._translateService.instant(
        'tkey;migrations.cisco.directory_numbers_report.table.utilization.empty',
      );
    } else if (this._dnReport.totalNumberOfCdrs && this.cdrDiffDays < 30) {
      this.utilizationSummary = this._translateService.instant(
        'tkey;migrations.number_report.table.utilization.short',
        {
          totalNumberOfCdrs: this._dnReport.totalNumberOfCdrs,
          timespan: this.cdrDiffDays,
          startDate: formattedOldestDate,
          endDate: formattedNewestDate,
        },
      );
    } else {
      this.utilizationSummary = this._translateService.instant(
        'tkey;migrations.number_report.table.utilization.in_use',
        {
          totalNumberOfCdrs: this._dnReport.totalNumberOfCdrs,
          timespan: this.cdrDiffDays,
          startDate: formattedOldestDate,
          endDate: formattedNewestDate,
        },
      );
    }
  }

  private _setSuggestedCallingProducts(): void {
    if (!this.cdrDiffDays || this.cdrDiffDays < 30) {
      MigrationsCiscoDirectoryNumbersReportComponent.DEFAULT_HIDDEN_COLUMNS.push(
        'tkey;migrations.cisco.directory_numbers_report.table.suggested_calling_product.label',
      );
    }

    this.groupedColumnFilterOptions = [
      ...this.groupedColumnFilterOptions,
      // AI Insights
      {
        value: 'tkey;migrations.cisco.directory_numbers_report.table.suggested_calling_product.label',
        label: 'tkey;migrations.cisco.directory_numbers_report.table.suggested_calling_product.label',
        groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.ai_insights.label',
        disabled: !this.cdrDiffDays || this.cdrDiffDays < 30,
        disabledTooltip: 'tkey;migrations.cisco.suggested_calling_products.insufficient_cdrs.tooltip',
      },
      {
        value: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
        label: 'tkey;migrations.cisco.directory_numbers_report.table.associated_cucm_user.label',
        groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.ai_insights.label',
      },
      {
        value: 'tkey;migrations.cisco.directory_numbers_report.table.hunt_list_membership.label',
        label: 'tkey;migrations.cisco.directory_numbers_report.table.hunt_list_membership.label',
        groupBy: 'tkey;migrations.cisco.directory_numbers_report.table.ai_insights.label',
      },
    ];

    this.initialGroupedColumnFilterOptions = [...this.groupedColumnFilterOptions].filter(
      (option: FilterSelectOption) =>
        !MigrationsCiscoDirectoryNumbersReportComponent.DEFAULT_HIDDEN_COLUMNS.includes(option.label),
    );
  }

  protected onRefreshClick(): void {
    this.isRefreshing = true;
    this.isManualRefresh = true;
    this._migrationsService.dispatchButtonState(this._refreshButtonId, {
      pending: true,
      buttonDisableState: {
        disabled: true,
      },
    });

    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: null,
      }),
    );

    forkJoin([
      this._migrationsCucmNumberReportContext.refresh(),
      this._migrationCucmHuntListsContext.refresh(),
    ]).subscribe();
  }

  private _removeRowUpdatedStyles(): void {
    this.data = this.data.map((row: CucmDnReportRow): CucmDnReportRow => {
      return {
        ...row,
        cssClass: 'transition-background-color',
        cellCssClass: {
          ...row.cellCssClass,
          displayName: '',
          userStatus: '',
          ldapUserSyncAttribute: '',
          ldapDirectoryName: '',
          firstName: '',
          lastName: '',
          mailId: '',
          ms365User: '',
          businessPhoneNumber: '',
        },
      };
    });
  }

  private _initContexts(): void {
    this._cucmE164TransformationRulesContext.setBaseUrl(this._tenantId, this._assessmentUuid);
    this._cucmE164TransformationRulesContext.refresh().subscribe();

    const ensureFreshReport$: Observable<void> = this._migrationsCucmNumberReportContext.hasData()
      ? this._migrationsCucmNumberReportContext.softRefresh()
      : of(undefined);

    const sub = ensureFreshReport$
      .pipe(
        switchMap(() =>
          combineLatest([
            this._migrationsAssessmentsContext.state$,
            this._migrationsCucmNumberReportContext.jobState$,
            this._migrationCucmHuntListsContext.jobState$,
            this._authenticationContext.state$,
          ]),
        ),
      )
      .subscribe(([migrations, dnsJobStatus, huntGroupsJobStatus, currentUser]) => {
        this._assessment = migrations.ciscoPbxExports.find(
          (migration: CiscoPbxExport) => migration.id === this._assessmentUuid,
        );

        this.isAutomationAllowed = this._authenticationContext.userHasPrivilege(
          currentUser,
          ZpmPrivilege.AUTOMATION,
          this._tenantId,
        );

        this._initBreadcrumbs();
        this._initBottomNav();
        this._handleContextPolling(dnsJobStatus, huntGroupsJobStatus);

        if (MigrationsService.shouldGetDnReport(dnsJobStatus, huntGroupsJobStatus)) {
          if (MigrationsService.isErroredStatus(dnsJobStatus, huntGroupsJobStatus)) {
            this.showCustomAlert = true;
            this.isLoading = false;
          } else {
            this.showCustomAlert = false;
            this._getReportData();
          }
          if (this.isManualRefresh) {
            this._toastService.push(
              ToastTypes.SUCCESS,
              ZiroIcons.REFRESH,
              'tkey;migrations.number_report.refresh_toast.title',
              'tkey;migrations.number_report.refresh_toast.message',
            );
            this.isManualRefresh = false;
          }
          this.isRefreshing = false;
        }
      });
    this._subscription.add(sub);
  }

  private _handleContextPolling(dnsJobStatus: JobStatus, huntGroupsJobStatus: JobStatus): void {
    const isDnJobStatusIdle = dnsJobStatus.jobState === JobState.IDLE;
    const isHuntGroupJobStatusIdle = huntGroupsJobStatus.jobState === JobState.IDLE;

    if (isDnJobStatusIdle && isHuntGroupJobStatusIdle) {
      this._migrationsService.setBottomNavTimestamp(
        dnsJobStatus.lastRunTime,
        'tkey;reports.microsoft.dial_plan_inventory.last_updated',
      );
      this._migrationsService.dispatchButtonState(this._refreshButtonId, {
        pending: false,
        buttonDisableState: {
          disabled: false,
        },
      });
    } else {
      this.isRefreshing = true;
      this._migrationsService.dispatchButtonState(this._refreshButtonId, {
        pending: true,
        buttonDisableState: {
          disabled: true,
        },
      });
      this._migrationsService.dispatchButtonState(this._exportButtonId, {
        pending: false,
        buttonDisableState: {
          disabled: true,
        },
      });
      this._migrationsService.dispatchButtonState(this._migrateButtonId, {
        pending: false,
        buttonDisableState: {
          disabled: true,
        },
      });
      this._migrationsService.dispatchButtonState(this._ignoreUnignoreRowsButtonId, {
        pending: false,
        buttonDisableState: {
          disabled: true,
        },
      });
    }
  }

  private _getReportData(): void {
    if (this._hasSubscribedToReportData) {
      return;
    }
    this._hasSubscribedToReportData = true;

    const subscription = combineLatest([
      this._migrationsCucmNumberReportContext.state$,
      this._migrationsCiscoSiteManagementContext.state$,
    ]).subscribe(([dnReport, sites]) => {
      this._dnReport = dnReport;
      this._sites = sites;
      const upns = Array.from(new Set(dnReport.entries.map((entry) => entry.toTeams?.toTeamsUser?.upn)));
      this._zpmUserPhotoContext.initUserPhotos(this._tenantId.toString(), upns).subscribe();

      this._setUtilizationSummary();
      this._setSuggestedCallingProducts();

      this._dnCallingSearchSpaceOptions = Array.from(
        new Set(dnReport.entries.map((entry: NumberReportEntry<CiscoFields>) => entry.pbxFields.callingSearchSpace)),
      ).filter((option) => !!option);

      this._routePartitionOptions = Array.from(
        new Set(dnReport.entries.map((entry: NumberReportEntry<CiscoFields>) => entry.pbxFields.routePartition)),
      ).filter((option) => !!option);

      this._devicePoolOptions = Array.from(
        new Set(
          dnReport.entries.flatMap((entry: NumberReportEntry<CiscoFields>) => {
            const associatedDevices = entry.pbxFields.line1Devices.length
              ? entry.pbxFields.line1Devices
              : entry.pbxFields.top20SecondaryLineDevices;
            return associatedDevices.map((line1Device) => line1Device.devicePool);
          }),
        ),
      ).filter((option) => !!option);

      this._deviceCssOptions = Array.from(
        new Set(
          dnReport.entries.flatMap((entry: NumberReportEntry<CiscoFields>) => {
            const associatedDevices = entry.pbxFields.line1Devices.length
              ? entry.pbxFields.line1Devices
              : entry.pbxFields.top20SecondaryLineDevices;
            return associatedDevices.map((line1Device) => line1Device.callingSearchSpace);
          }),
        ),
      ).filter((option) => !!option);

      this._uniqueHuntListMemberships = Array.from(
        new Set(
          dnReport.entries.flatMap(
            (entry: NumberReportEntry<CiscoFields>) => entry.numberInsights.callQueueMemberships,
          ),
        ),
      );

      this._siteOptions = Array.from(
        new Set(dnReport.entries.map((entry: NumberReportEntry<CiscoFields>) => this._getSiteDisplayValue(entry.site))),
      ).filter((option) => !!option);
      this.siteOptionsWithCounts = this._toFilterOptions(this._siteOptions);

      this._migrationStatusOptions = Array.from(MigrationsService.MIGRATION_STATUS_ROW_MAP.values()).map(
        (migrationStatus) => migrationStatus.label,
      );

      if (!this._areFiltersInitialized) {
        this._areFiltersInitialized = true;
        this._initFilterOptionLists();
      }
      this._buildRows(dnReport.entries);
      this.isLoading = false;
    });
    this._subscription.add(subscription);
  }

  private _getEndUserIconHtml(userCount: number): string {
    if (userCount === 1) {
      return `<i class="${ZiroIcons.CIRCLE_CHECK_SOLID} text-success"></i>`;
    } else if (userCount > 1) {
      return `
        <span class="stacked-container">
          <i class="${ZiroIcons.CIRCLE} text-danger"></i>
          <span class="stacked-container-element text-white">${userCount}</span>
        </span>
      `;
    }

    return '';
  }

  private _getCucmEndUserFieldOptionHtml(dnUser: CiscoNumberReportEndUser): string {
    if (!dnUser) {
      return '';
    }

    const bulletedList = dnUser.associations
      .map((association: AssociationEnum) => {
        switch (association) {
          case AssociationEnum.IPCC_EXTENSION: {
            return `<li>${this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.table.cucm_end_user.field.enabled.option.agent.label')}</li>`;
          }
          case AssociationEnum.PRIMARY_EXTENSION: {
            return `<li>${this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.table.cucm_end_user.field.enabled.option.primary.label')}</li>`;
          }
          case AssociationEnum.OWNED_DEVICE_LINE_1: {
            return `<li>${this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.table.cucm_end_user.field.enabled.option.owned.label')}</li>`;
          }
          case AssociationEnum.ASSOCIATED_DEVICE_LINE_1: {
            return `<li>${this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.table.cucm_end_user.field.enabled.option.associated.label')}</li>`;
          }
          case AssociationEnum.DN_DESCRIPTION_NAME: {
            return `<li>${this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.table.cucm_end_user.field.enabled.option.dn_description.label')}</li>`;
          }
          case AssociationEnum.DN_ALERTING_NAME: {
            return `<li>${this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.table.cucm_end_user.field.enabled.option.dn_alerting_name.label')}</li>`;
          }
          default: {
            break;
          }
        }
      })
      .join('');

    return `
        <ul class="mb-0 line-height-1 table-dropdown-option-sub-item-font-size fw-normal">
          ${bulletedList}   
        </ul>
      `;
  }

  private _buildBadgeTooltipMarkup(strings: string[], tooltipTkey: string): string {
    const translatedTkey = this._translateService.instant(tooltipTkey);
    const formattedStrings = strings.map((string) => `<li>${string}</li>`).join('');

    return `
    <div>
      <div>${translatedTkey}</div>
      <ul class="text-start mb-0">
      ${formattedStrings}
      </ul>
    </div>`;
  }

  private _buildBadges(
    strings: string[],
    maxChars: number,
    tooltipTkey: string,
    type: string,
  ): { badges: DatatableBadge<CucmDnReportRow>[]; stringsTooLong: boolean } {
    if (!strings.length) {
      return { badges: [], stringsTooLong: false };
    }

    const totalChars = strings.reduce((sum, string) => sum + string.length, 0);

    if (totalChars <= maxChars) {
      return { badges: strings.map((name) => ({ label: name })), stringsTooLong: false };
    }

    const moreTkey: string = this._translateService.instant('tkey;datatable.badge.more.label');
    const badges: DatatableBadge<CucmDnReportRow>[] = [];
    let remainingStrings = strings.length;
    let totalCharsInBadgesSoFar = 0;

    for (let i = 0; i < strings.length; i++) {
      const stringLength: number = strings[i].length;

      if (moreTkey.length + stringLength + remainingStrings.toString().length + totalCharsInBadgesSoFar <= maxChars) {
        badges.push({ label: strings[i] });
        totalCharsInBadgesSoFar += stringLength;
        remainingStrings--;
      } else {
        break;
      }
    }

    const moreLabel = remainingStrings + moreTkey;
    const namesTooLong = strings.length > 1 && !badges.length;

    if (badges.length) {
      badges.push({
        label: moreLabel,
        hardcodedTooltip: this._buildBadgeTooltipMarkup(strings.slice(strings.length - remainingStrings), tooltipTkey),
      });
    } else if (namesTooLong) {
      badges.push({
        label: this._translateService.instant(
          'tkey;migrations.cisco.directory_numbers_report.table.badge.x_found.label',
          {
            typeCount: strings.length,
            type,
          },
        ),
        hardcodedTooltip: this._buildBadgeTooltipMarkup(strings, tooltipTkey),
      });
    } else {
      badges.push({
        label: strings[0],
        showTooltipOnTruncate: true,
      });
    }

    return { badges, stringsTooLong: namesTooLong };
  }

  private _getSiteStatusLabelTkey(siteStatus: SiteStatus): string {
    switch (siteStatus) {
      case SiteStatus.UNMAPPED:
        return 'tkey;migrations.cisco.directory_numbers_report.table.site.unmapped.label';
      case SiteStatus.SOURCE_FIELD_EMPTY:
        return 'tkey;migrations.cisco.directory_numbers_report.table.site.source_field_empty.label';
      case SiteStatus.INDETERMINATE:
        return 'tkey;migrations.cisco.directory_numbers_report.table.site.indeterminate.label';
      default:
        return '';
    }
  }

  private _getSiteStatusTooltipTkey(siteStatus: SiteStatus): string {
    switch (siteStatus) {
      case SiteStatus.UNMAPPED:
        return 'tkey;migrations.cisco.directory_numbers_report.table.site.unmapped.tooltip';
      case SiteStatus.SOURCE_FIELD_EMPTY:
        return 'tkey;migrations.cisco.directory_numbers_report.table.site.source_field_empty.tooltip';
      case SiteStatus.INDETERMINATE:
        return 'tkey;migrations.cisco.directory_numbers_report.table.site.indeterminate.tooltip';
      default:
        return '';
    }
  }

  private _getSiteDisplayValue(site: SiteResolution): string {
    if (site.siteStatus === SiteStatus.RESOLVED) {
      return this._sites.find((configuredSite) => configuredSite.siteId === site.siteId)?.name ?? site.siteId;
    }
    return this._translateService.instant(this._getSiteStatusLabelTkey(site.siteStatus));
  }

  private _getSiteStatusIconHtml(site: SiteResolution): string {
    const icons: string[] = [];

    if (site.siteSource === SiteSource.USER_ENTRY) {
      icons.push(`<i class="${ZiroIcons.LOCATION}"></i>`);
    }

    switch (site.siteStatus) {
      case SiteStatus.RESOLVED:
        icons.push(`<i class="${ZiroIcons.CIRCLE_CHECK_SOLID} text-success"></i>`);
        break;
      case SiteStatus.UNMAPPED:
        icons.push(`<i class="${ZiroIcons.CIRCLE_EXCLAMATION} text-warning"></i>`);
        break;
      case SiteStatus.INDETERMINATE:
        icons.push(`<i class="${ZiroIcons.CIRCLE_EXCLAMATION} text-danger"></i>`);
        break;
      default:
        break;
    }

    return icons.join(' ');
  }

  private _getSiteOptions(): FilterSelectOption[] {
    return this._sites
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((configuredSite) => ({
        value: configuredSite.siteId,
        label: configuredSite.name,
        optionHtml: `<span class="fw-normal">${configuredSite.name}</span>`,
      }));
  }

  private _getSiteField(site: SiteResolution, rowId: string): DatatableSelectField {
    const displayValue = this._getSiteDisplayValue(site);
    const statusIconHtml = this._getSiteStatusIconHtml(site);
    const isResolved = site.siteStatus === SiteStatus.RESOLVED;
    const tooltip = isResolved ? '' : this._translateService.instant(this._getSiteStatusTooltipTkey(site.siteStatus));

    const currentOption: FilterSelectOption = {
      value: displayValue,
      label: displayValue,
      optionHtml: `<span class="fw-normal">${displayValue}</span>`,
      labelHtml: `
        <div class="d-flex justify-content-between">
          <span class="overflow-hidden select-field-text${isResolved ? '' : ' text-muted'}">${displayValue}</span>
          <span class="select-field-icon" style="padding-left: 2px;">
            ${statusIconHtml}
          </span>
        </div>
      `,
    };

    return {
      type: DatatableFieldTypes.SELECT,
      options: [currentOption, ...this._getSiteOptions().filter((option) => option.value !== site.siteId)],
      showHtml: true,
      value: displayValue,
      isClearable: site.siteSource === SiteSource.USER_ENTRY,
      onSelect: (opt: FilterSelectOption) => {
        if (opt.value === displayValue) {
          return;
        }
        this._onSiteOverrideSelect(rowId, opt.value);
      },
      onClear: () => this._onSiteOverrideClear(rowId),
      tooltip,
      tooltipDisabled: !tooltip,
    };
  }

  private _onSiteOverrideSelect(rowId: string, siteId: string): void {
    this._migrationsCucmNumberReportContext.updateDirectoryNumberSiteOverride(rowId, siteId).subscribe({
      error: () => this._migrationsCucmNumberReportContext.softRefresh().subscribe(),
    });
  }

  private _onSiteOverrideClear(rowId: string): void {
    this._migrationsCucmNumberReportContext.removeDirectoryNumberSiteOverride(rowId).subscribe({
      error: () => this._migrationsCucmNumberReportContext.softRefresh().subscribe(),
    });
  }

  private _buildRows(entries: NumberReportEntry<CiscoFields>[]): void {
    const rows = entries.map((entry: NumberReportEntry<CiscoFields>): CucmDnReportRow => {
      const rowKey = entry.rowId;

      const [endUser, userOptions, endUserTooltip]: [CiscoNumberReportEndUser | null, FilterSelectOption[], string] =
        entry.pbxFields.users.length
          ? this._buildEndUser(entry)
          : [
              null,
              [],
              this._translateService.instant(
                'tkey;migrations.cisco.directory_numbers_report.table.cucm_end_user.field.disabled.tooltip',
              ),
            ];

      const associatedDevices = entry.pbxFields.line1Devices.length
        ? entry.pbxFields.line1Devices
        : entry.pbxFields.top20SecondaryLineDevices;

      const {
        associatedDeviceInfo: cssAssociatedDeviceInfo,
        sharedDevices: cssSharedDevices,
        allSameSharedValue: cssAllSameSharedValue,
        value: cssValue,
        tooltip: cssTooltip,
      } = this._buildAssociatedDevices(associatedDevices, 'callingSearchSpace');
      const {
        associatedDeviceInfo: devicePoolAssociatedDeviceInfo,
        sharedDevices: devicePoolSharedDevices,
        allSameSharedValue: devicePoolAllSameSharedValue,
        value: devicePoolValue,
        tooltip: devicePoolTooltip,
      } = this._buildAssociatedDevices(associatedDevices, 'devicePool');
      const {
        associatedDeviceInfo: externalMaskAssociatedDeviceInfo,
        sharedDevices: externalMaskSharedDevices,
        allSameSharedValue: externalMaskAllSameSharedValue,
        value: externalMaskValue,
        tooltip: externalMaskTooltip,
      } = this._buildAssociatedDevices(associatedDevices, 'externalPhoneNumberMask');
      const {
        associatedDeviceInfo: lineDisplayAssociatedDeviceInfo,
        sharedDevices: lineDisplaySharedDevices,
        allSameSharedValue: lineDisplayAllSameSharedValue,
        value: lineDisplayValue,
        tooltip: lineDisplayTooltip,
      } = this._buildAssociatedDevices(associatedDevices, 'lineDisplay');
      const teamsNumberObject = this._getMoveToTeamsNumberObject(
        entry.toTeams?.toTeamsNumber,
        entry.pbxFields?.directoryNumber,
        entry.rowId,
        entry.migrationStatus,
      );

      const computedExternalMaskTooltip = this._getAssociatedDeviceTooltip(
        externalMaskValue,
        externalMaskTooltip,
        associatedDevices,
        'External Mask',
      );
      const computedLineDisplayTooltip = this._getAssociatedDeviceTooltip(
        lineDisplayValue,
        lineDisplayTooltip,
        associatedDevices,
        'Line Display',
      );

      const suggestedProductTooltipData = this._getSuggestedCallingProductTooltip(entry);
      const line1AssociatedDevicesTooltip =
        'tkey;migrations.cisco.directory_numbers_report.table.line_1_associated_devices.tooltip.header';
      const secondaryLineAssociatedDevicesTooltip =
        'tkey;migrations.cisco.directory_numbers_report.table.secondary_line_associated_devices.tooltip.header';

      const checkboxDisabledTooltip =
        entry.toTeams.toTeamsUser.userStatus === UserStatusV2.MIGRATED_ENTERPRISE_VOICE_ENABLED
          ? 'tkey;migrations.cisco.directory_numbers_report.table.checkbox.disabled.migrated_user'
          : null;

      return {
        id: rowKey,
        isTableRowSelectDisabled: !!checkboxDisabledTooltip,
        tableRowSelectDisabledTooltip: checkboxDisabledTooltip,
        ...this._getDirectoryNumberCellValues(entry),
        ...this._getEndUserCellValues(endUser),
        endUsers: entry.pbxFields.users,
        huntListMembership: entry.numberInsights?.callQueueMemberships?.length
          ? entry.numberInsights.callQueueMemberships.sort().join(',')
          : '',
        site: this._getSiteDisplayValue(entry.site),
        // Associated Line 1 Devices
        associatedLine1Name: entry.pbxFields.line1Devices.map((device) => device.name),
        associatedSecondaryLineName: entry.pbxFields.top20SecondaryLineDevices.map((device) => device.name),
        associatedDeviceCss: cssValue ?? '',
        devicePool: devicePoolValue ?? '',
        externalMask: externalMaskValue ?? '',
        lineDisplay: lineDisplayValue ?? '',
        // To Teams
        teamsUser: entry.toTeams.toTeamsUser.upn,
        isTeamsUserMigrated: entry.toTeams.toTeamsUser.userStatus === UserStatusV2.MIGRATED_ENTERPRISE_VOICE_ENABLED,
        teamsNumber: entry.toTeams.toTeamsNumber.number,
        teamsNumberObject: entry.toTeams.toTeamsNumber,
        isTableRowSelectable: true,
        html: {
          lineType: this._buildLineDisplayMarkup(entry.numberInsights.lineUsageType),
        },
        fields: {
          cucmEndUser: endUser?.userId
            ? {
                type: DatatableFieldTypes.SELECT,
                options: [...userOptions],
                value: endUser.userId,
                showHtml: true,
                tooltip: endUserTooltip,
                tooltipDisabled: !endUserTooltip,
                validation: [
                  {
                    validator: (): ZiroFormsValidationState => {
                      return userOptions.length > 1 ? ZiroFormsValidationState.WARNING : ZiroFormsValidationState.VALID;
                    },
                    message: () => '',
                  },
                ],
              }
            : {
                type: DatatableFieldTypes.SELECT,
                options: [] as FilterSelectOption[],
                value: this._translateService.instant(
                  'tkey;migrations.cisco.directory_numbers_report.table.cucm_end_user.field.disabled.label',
                ),
                isDisabled: true,
                tooltip: endUserTooltip,
                tooltipDisabled: !endUserTooltip,
              },
          site: this._getSiteField(entry.site, entry.rowId),
          // Associated Devices
          associatedDeviceCss: this._getAssociatedDeviceField(associatedDevices, 'callingSearchSpace', {
            associatedDeviceInfo: cssAssociatedDeviceInfo,
            sharedDevices: cssSharedDevices,
            allSameSharedValue: cssAllSameSharedValue,
            value: cssValue,
            tooltip: cssTooltip,
          }),
          devicePool: this._getAssociatedDeviceField(associatedDevices, 'devicePool', {
            associatedDeviceInfo: devicePoolAssociatedDeviceInfo,
            sharedDevices: devicePoolSharedDevices,
            allSameSharedValue: devicePoolAllSameSharedValue,
            value: devicePoolValue,
            tooltip: devicePoolTooltip,
          }),
          externalMask: this._getAssociatedDeviceField(associatedDevices, 'externalPhoneNumberMask', {
            associatedDeviceInfo: externalMaskAssociatedDeviceInfo,
            sharedDevices: externalMaskSharedDevices,
            allSameSharedValue: externalMaskAllSameSharedValue,
            value: externalMaskValue,
            tooltip: computedExternalMaskTooltip,
          }),
          lineDisplay: this._getAssociatedDeviceField(associatedDevices, 'lineDisplay', {
            associatedDeviceInfo: lineDisplayAssociatedDeviceInfo,
            sharedDevices: lineDisplaySharedDevices,
            allSameSharedValue: lineDisplayAllSameSharedValue,
            value: lineDisplayValue,
            tooltip: computedLineDisplayTooltip,
          }),
          // To Teams
          teamsUser: {
            type: DatatableFieldTypes.SELECT,
            value: entry.toTeams.toTeamsUser.upn
              ? {
                  value: entry.toTeams.toTeamsUser.upn,
                  label: entry.toTeams.toTeamsUser.upn,
                }
              : null,
            showHtml: true,
            asyncOptionsFn: (query) => {
              if (!query || query.length < 3) {
                return of([]);
              }
              return this._microsoft365UserResource.search(this._assessment.tenantId?.toString(), query).pipe(
                delayWhen((users) => {
                  if (!users.length) {
                    return of([]);
                  }
                  // Only include the first 15 results. The photo search endpoint is pretty unoptimized.
                  const upns = users.map((user) => user.microsoft365UserRef.id).slice(0, 15);
                  return this._zpmUserPhotoContext.initUserPhotos(this._assessment.tenantId?.toString(), upns);
                }),
                map((users) => {
                  const photos = this._zpmUserPhotoContext.getUserPhotos();
                  return users.map((result) => {
                    const photo = photos.find(
                      (p) => p.userPrincipalName === result.microsoft365UserRef.id,
                    )?.photoBase64;
                    const image = photo
                      ? `<img src="data:image/png;base64,${photo}" class="display-pic-size user-profile-picture" alt="display picture"/>`
                      : `<i class="display-pic-size no-user-icon ${ZiroIcons.CIRCLE_USER}"></i>`;
                    return {
                      value: result.microsoft365UserRef.id,
                      label: result.microsoft365UserRef.id,
                      optionHtml: `${image} <span class="ms-4">${result.microsoft365UserRef.id} | ${result.microsoft365UserRef.displayName}</span>`,
                    };
                  });
                }),
              );
            },
            base64ImageSearchFn: (value) => {
              if (!value) {
                return of(null);
              }
              return new Observable((subscriber) => {
                const loadingIcon = `<i class="position-absolute text-warning ${this.ZiroIcons.LOADING}"></i>`;
                subscriber.next(loadingIcon);
                this._zpmUserPhotoContext
                  .initUserPhotos(this._assessment.tenantId?.toString(), [value])
                  .pipe(
                    switchMap(() => {
                      if (entry.toTeams.toTeamsUser?.upn !== value) {
                        return this._migrationsCucmNumberReportContext.state$.pipe(
                          map(
                            (report) =>
                              report.entries.find(
                                (e) =>
                                  e.pbxFields.directoryNumber === entry.pbxFields.directoryNumber &&
                                  e.pbxFields.routePartition === entry.pbxFields.routePartition,
                              )?.toTeams.toTeamsUser,
                          ),
                          filter((toTeamsUser) => toTeamsUser?.upn === value),
                          take(1),
                        );
                      }
                      return of(entry.toTeams.toTeamsUser);
                    }),
                    map(() => {
                      const photo = this._zpmUserPhotoContext
                        .getUserPhotos()
                        .find((photo) => photo.userPrincipalName === value)?.photoBase64;
                      return photo
                        ? `<img src="data:image/png;base64,${photo}" class="display-pic-size user-profile-picture" alt="display picture"/>`
                        : `
                  <span class="display-pic-size">
                    <svg
                      width="100%"
                      height="100%"
                      viewBox="0 0 2667 2667"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                      xml:space="preserve"
                      xmlns:serif="http://www.serif.com/"
                      style="
                        fill-rule: evenodd;
                        clip-rule: evenodd;
                        stroke-linecap: round;
                        stroke-linejoin: round;
                        stroke-miterlimit: 1.5;
                      "
                    >
                      <path
                        d="M1929.167,1867.5c-92.083,-160 -265,-267.5 -462.5,-267.5l-266.667,0c-197.5,0 -370.417,107.5 -462.5,267.5c146.667,163.333 359.167,265.833 595.833,265.833c236.667,0 449.167,-102.917 595.833,-265.833Zm-1662.5,-534.167c-0,-589.167 477.5,-1066.667 1066.667,-1066.667c589.167,-0 1066.667,477.5 1066.667,1066.667c0,589.167 -477.5,1066.667 -1066.667,1066.667c-589.167,0 -1066.667,-477.5 -1066.667,-1066.667Zm1066.667,66.667c165.833,0 300,-134.167 300,-300c0,-165.833 -134.167,-300 -300,-300c-165.833,0 -300,134.167 -300,300c0,165.833 134.167,300 300,300Z"
                        style="fill-rule: nonzero"
                      />
                      <circle cx="1333.333" cy="1333.333" r="1100" style="fill:none;stroke-width:187.5px;" />
                    </svg>
                  </span>
                  `;
                    }),
                  )
                  .subscribe((html) => {
                    subscriber.next(html);
                    subscriber.complete();
                  });
              });
            },
            imageBorderClass: () => this._getUpnPhotoBorderColor(entry.toTeams.toTeamsUser.userStatus),
            isClearable: entry.toTeams.toTeamsUser.teamsUserSource === TeamsUserSource.USER_ENTRY,
            onSelect: (opt: FilterSelectOption) => this._onMs365UserInputSelect(entry, opt.value),
            onClear: () => this._onMs365UserInputSelect(entry, ''),
            tooltip:
              entry.migrationStatus === MigrationStatusV2.IGNORED
                ? this._translateService.instant('tkey;migrations.number_report.ignored.row.disabled.tooltip')
                : entry.toTeams.toTeamsUser.userStatus !== UserStatusV2.NO_STATUS &&
                    entry.toTeams.toTeamsUser.userStatus !== UserStatusV2.VALID_READY &&
                    entry.toTeams.toTeamsUser.userStatus !== UserStatusV2.MIGRATED_ENTERPRISE_VOICE_ENABLED
                  ? entry.toTeams.toTeamsUser.statusMessage
                      ?.replace(
                        'Microsoft Teams Phone Standard',
                        '<i><strong>Microsoft Teams Phone Standard</strong></i>',
                      )
                      .replace('Enterprise Voice', '<i><strong>Enterprise Voice</strong></i>')
                  : null,
            isDisabled: entry.migrationStatus === MigrationStatusV2.IGNORED,
            validation: [
              {
                validator: () => {
                  return [
                    UserStatusV2.NO_STATUS,
                    UserStatusV2.VALID_READY,
                    UserStatusV2.MIGRATED_ENTERPRISE_VOICE_ENABLED,
                    UserStatusV2.STAGED_IN_BULK_DRAFT,
                  ].includes(entry.toTeams.toTeamsUser.userStatus)
                    ? ZiroFormsValidationState.VALID
                    : ZiroFormsValidationState.INVALID;
                },
                message: () => '',
              },
            ],
          },
          teamsNumber: teamsNumberObject,
          teamsNumberObject: {
            type: DatatableFieldTypes.TEXT,
            value: `${entry.toTeams.toTeamsNumber.teamsNumberSource}_${entry.toTeams.toTeamsNumber.numberStatus}`,
            isHidden: true,
          },
        },
        badges: {
          associatedLine1Name: {
            maxLength: this.MAX_ASSOCIATED_DEVICE_BADGE_LENGTH,
            values: this._buildAssociatedDeviceBadges(entry.pbxFields.line1Devices, line1AssociatedDevicesTooltip),
          },
          associatedSecondaryLineName: {
            maxLength: this.MAX_ASSOCIATED_DEVICE_BADGE_LENGTH,
            values: this._buildAssociatedDeviceBadges(
              entry.pbxFields.top20SecondaryLineDevices,
              secondaryLineAssociatedDevicesTooltip,
            ),
          },
          huntListMembership: {
            values: this._buildBadges(
              entry.numberInsights.callQueueMemberships,
              this.columns.find((col) => col.name === 'huntListMembership').columnCharacterWidth,
              'tkey;migrations.cisco.directory_numbers_report.table.hunt_list_membership.tooltip',
              'memberships',
            ).badges,
            maxLength: this.columns.find((col) => col.name === 'huntListMembership').columnCharacterWidth,
          },
        },
        hardcodedTooltips: {
          ldapUserSyncAttribute:
            endUser?.ldapUserSyncAttribute === LdapUserSyncAttribute.LOCAL_USER
              ? this._translateService.instant(
                  'tkey;migrations.cisco.directory_numbers_report.table.ldap_sync_attribute.local.tooltip',
                )
              : '',
          suggestedCallingProduct: suggestedProductTooltipData.tooltip,
        },
        diffInDays: suggestedProductTooltipData.diffInDays,
        hardcodedDisableTooltip: {
          ldapUserSyncAttribute: endUser?.ldapUserSyncAttribute !== LdapUserSyncAttribute.LOCAL_USER,
        },
        borderClass: {
          user: 'move-to-teams-user-col',
          number: 'move-to-teams-number-col',
        },
      };
    });

    const UILineTypeEntries = Object.entries(UiLineType);

    this.data = rows.sort((a, b) => {
      const aLineUsageType = UILineTypeEntries.find((entry) => entry[1] === a.lineType)[0];
      const bLineUsageType = UILineTypeEntries.find((entry) => entry[1] === b.lineType)[0];

      const aOrder = MigrationsService.LINE_TYPE_ROW_MAP_V2.get(aLineUsageType as LineUsageTypeV2).initialSortOrder;
      const bOrder = MigrationsService.LINE_TYPE_ROW_MAP_V2.get(bLineUsageType as LineUsageTypeV2).initialSortOrder;
      return aOrder - bOrder;
    });

    this._updateFilterCounts();
  }

  private _onMs365UserInputSelect(entry: NumberReportEntry<CiscoFields>, newUpn: string): void {
    const cucmDnReportRow = this.data.find((row) => row.directoryNumber === entry.pbxFields.directoryNumber);
    cucmDnReportRow.isMigrationStatusPending = true;
    this._migrationsCucmNumberReportContext.updateDirectoryNumberUpn(entry.rowId, newUpn).subscribe();
  }

  private _getUpnPhotoBorderColor(userStatus: UserStatusV2): string {
    switch (userStatus) {
      case UserStatusV2.VALID_READY:
      case UserStatusV2.NO_STATUS:
        return '';
      case UserStatusV2.MIGRATED_ENTERPRISE_VOICE_ENABLED:
        return 'teams-user--migrated';
      case UserStatusV2.STAGED_IN_BULK_DRAFT:
        return 'teams-user--staged';
      default:
        return 'teams-user--error';
    }
  }

  private _getAssociatedDeviceTooltip(
    hasValue: string,
    fallbackTooltip: string,
    lineDevices: LineDevice[],
    associatedDeviceType: 'External Mask' | 'Line Display',
  ): string {
    if (lineDevices.length === 0) {
      return this._translateService.instant(
        'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.field.disabled.tooltip',
      );
    }

    const tooltipHtmlContainerStart = `<ul>This directory number is on a line of the devices listed below, but none have <strong><em>${associatedDeviceType}</em></strong> configured:`;
    const tooltipHtmlContainerEnd = `</ul>`;

    const deviceNames = lineDevices.map((device) => `<li><strong>${device.name}</strong></li>`).join('');

    return hasValue ? fallbackTooltip : tooltipHtmlContainerStart + deviceNames + tooltipHtmlContainerEnd;
  }

  private _buildEndUser(
    entry: NumberReportEntry<CiscoFields>,
  ): [CiscoNumberReportEndUser | null, FilterSelectOption[], string] {
    const sortedUsers: CiscoNumberReportEndUser[] = entry.pbxFields.users.sort(
      (a: CiscoNumberReportEndUser, b: CiscoNumberReportEndUser) => {
        const isAAgent = a.associations.includes(AssociationEnum.IPCC_EXTENSION) ? 0 : 1;
        const isBAgent = b.associations.includes(AssociationEnum.IPCC_EXTENSION) ? 0 : 1;

        if (isAAgent !== isBAgent) {
          return isAAgent - isBAgent;
        }

        return b.associations.length - a.associations.length;
      },
    );
    const endUser = sortedUsers[0] ?? null;
    const userOptions = sortedUsers.map((user: CiscoNumberReportEndUser) => {
      const optionHtml = this._getCucmEndUserFieldOptionHtml(user);
      const getStatusIconHtml = this._getEndUserIconHtml(sortedUsers.length);

      const nameText =
        user.firstName && user.lastName ? `${user.userId} (${user.firstName} ${user.lastName})` : user.userId;

      return {
        label: user.userId,
        value: user.userId,
        labelHtml: `
          <div class="d-flex justify-content-between">
            <span class="overflow-hidden select-field-text">${user.userId}</span>    
            <span class="select-field-icon" style="padding-left: 2px;">
              ${getStatusIconHtml}
            </span>
          </div>
        `,
        optionHtml: `
          <span class="d-flex justify-content-between fw-normal">
            <span><strong>${nameText}</strong> ${this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.table.cucm_end_user.field.enabled.option.content')}</span>
          </span>
          <div>
            ${optionHtml}
          </div>
        `,
      };
    });

    const endUserTooltip =
      userOptions.length > 1
        ? 'tkey;migrations.cisco.directory_numbers_report.table.cucm_end_user.field.enabled.tooltip.content.multiple'
        : 'tkey;migrations.cisco.directory_numbers_report.table.cucm_end_user.field.enabled.tooltip.content.unique';

    return [endUser, userOptions, this._translateService.instant(endUserTooltip)];
  }

  private _buildAssociatedDevices(
    lineDevices: LineDevice[],
    lineDeviceProperty: keyof LineDevice,
  ): AssociatedDeviceFieldParams {
    const associatedDeviceInfo = lineDevices.reduce(
      (acc: { [key: string]: LineDevice[] }, curDevice: LineDevice): { [key: string]: LineDevice[] } => {
        if (!acc[curDevice[lineDeviceProperty]]) {
          acc[curDevice[lineDeviceProperty]] = [curDevice];
        } else {
          acc[curDevice[lineDeviceProperty]].push(curDevice);
        }
        return acc;
      },
      {},
    );

    const [sharedName, sharedDevices] = Object.entries(associatedDeviceInfo).find(
      (associatedDeviceInfoCount) => associatedDeviceInfoCount[1].length > 1,
    ) ?? ['', []];

    const nonEmptyLineDevices = cloneDeep(
      lineDevices.filter((lineDevice: LineDevice) => lineDevice[lineDeviceProperty].trim().length),
    );
    const allSameSharedValue = sharedDevices.length > 1 && sharedDevices.length === nonEmptyLineDevices.length;

    const tooltip =
      allSameSharedValue || nonEmptyLineDevices.length === 1
        ? this._translateService.instant(
            'tkey;migrations.cisco.directory_numbers_report.table.associated_device.field.enabled.tooltip.content.same',
          )
        : nonEmptyLineDevices.length
          ? this._translateService.instant(
              'tkey;migrations.cisco.directory_numbers_report.table.associated_device.field.enabled.tooltip.content.shared',
            )
          : this._translateService.instant(
              'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.field.disabled.tooltip',
            );

    const value =
      sharedDevices.length > 1 ? sharedName : (Object.keys(associatedDeviceInfo).find((key) => key?.trim()) ?? '');

    return { associatedDeviceInfo, sharedDevices, allSameSharedValue, value, tooltip };
  }

  private _getAssociatedDeviceField(
    lineDevices: LineDevice[],
    lineDeviceProperty: keyof LineDevice,
    fieldParams: AssociatedDeviceFieldParams,
  ): DatatableSelectField {
    const { associatedDeviceInfo, sharedDevices, allSameSharedValue, tooltip, value } = fieldParams;

    if (!lineDevices.length || !value) {
      return {
        type: DatatableFieldTypes.SELECT,
        options: [] as FilterSelectOption[],
        value: !lineDevices.length
          ? this._translateService.instant(
              'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.field.disabled.label',
            )
          : this._translateService.instant(
              'tkey;migrations.cisco.directory_numbers_report.table.associated_devices.field.none_configured.label',
            ),
        isDisabled: true,
        tooltip: tooltip,
      };
    }

    const nonEmptyLineDevices = cloneDeep(
      lineDevices.filter((line1Device: LineDevice) => line1Device[lineDeviceProperty].trim().length),
    );

    sharedDevices.forEach((sharedDevice, i) => {
      if (i === sharedDevices.length - 1) {
        return;
      }
      const targetIndex = nonEmptyLineDevices.findIndex(
        (device) => device[lineDeviceProperty] === sharedDevice[lineDeviceProperty],
      );
      nonEmptyLineDevices.splice(targetIndex, 1);
    });

    const options = nonEmptyLineDevices.map((lineDevice) => {
      let optionHtml = `
        <span class="d-flex fw-normal">
          <strong>${lineDevice[lineDeviceProperty]}</strong>&nbsp;${this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.table.associated_device.field.enabled.option.content')}    
        </span>
      `;

      const singleDeviceMatch = nonEmptyLineDevices.length === 1;

      let statusIconHtml;
      if (allSameSharedValue || singleDeviceMatch) {
        statusIconHtml = `<i class="${ZiroIcons.CIRCLE_CHECK_SOLID} text-success"></i>`;
        if (singleDeviceMatch) {
          optionHtml += `
        <div>
          <ul class="mb-0 line-height-1 table-dropdown-option-sub-item-font-size fw-normal">
            ${associatedDeviceInfo[lineDevice[lineDeviceProperty]].map((device) => `<li><strong>${device.name}</strong> - ${device.description}</li>`).join('')}
          </ul>
        </div>
        `;
        } else {
          optionHtml += `
        <div>
          <ul class="mb-0 line-height-1 table-dropdown-option-sub-item-font-size fw-normal">
            ${sharedDevices.map((device) => `<li><strong>${device.name}</strong> - ${device.description}</li>`).join('')}
          </ul>
        </div>
        `;
        }
      } else {
        statusIconHtml = `
          <span class="stacked-container">
            <i class="${ZiroIcons.CIRCLE} text-danger"></i>
            <span class="stacked-container-element text-white">${nonEmptyLineDevices.length}</span>
          </span>
        `;

        optionHtml += `
        <div>
          <ul class="mb-0 line-height-1 table-dropdown-option-sub-item-font-size fw-normal">
            ${associatedDeviceInfo[lineDevice[lineDeviceProperty]].map((device) => `<li><strong>${device.name}</strong> - ${device.description}</li>`).join('')}
          </ul>
        </div>
        `;
      }

      return {
        label: lineDevice[lineDeviceProperty],
        value: lineDevice[lineDeviceProperty],
        labelHtml: `
              <div class="d-flex justify-content-between">
                <span class="overflow-hidden select-field-text">${lineDevice[lineDeviceProperty]}</span>  
                <span class="select-field-icon" style="padding-left: 2px;">
                  ${statusIconHtml}
                </span>
              </div>
            `,
        optionHtml,
      };
    });

    return {
      type: DatatableFieldTypes.SELECT,
      showHtml: true,
      options,
      value,
      tooltip,
      isCopiable: true,
      onSelect: (opt: FilterSelectOption) =>
        this._updateDevicePoolCssFilterCounts(value, opt.value, lineDeviceProperty),
    };
  }

  private _getMoveToTeamsNumberObject(
    teamsObject: ToTeamsNumberV2,
    dn: string,
    rowId: string,
    migrationStatus: MigrationStatusV2,
  ): DatatableTextField {
    const maxLength = 16;
    const cellWidth = '21ch';
    const onBlur = (inputValue: string, fieldState: DatatableTextField): void => {
      const oldIconHtmlPrepend = fieldState.iconHtmlPrepend;
      fieldState.iconHtmlPrepend = `<i class="text-warning fa-xl fa ${this.ZiroIcons.LOADING}"></i>`;
      let putValue = inputValue.trim();
      if (putValue === '+') {
        putValue = dn;
      }
      if (inputValue.trim() !== teamsObject.number) {
        fieldState.value = '';
        const cucmDnReportRow = this.data.find((row) => row.directoryNumber === dn);
        cucmDnReportRow.isMigrationStatusPending = true;
        this._migrationsCucmNumberReportContext
          .updateDirectoryNumberTeamsNumber(rowId, putValue)
          .subscribe(() => (fieldState.iconHtmlPrepend = oldIconHtmlPrepend));
      } else {
        fieldState.iconHtmlPrepend = oldIconHtmlPrepend;
      }
    };
    const validation = [
      {
        validator: (): ZiroFormsValidationState =>
          teamsObject?.numberStatus !== NumberStatusV2.VALID_FORMAT
            ? ZiroFormsValidationState.INVALID
            : ZiroFormsValidationState.VALID,
        message: () =>
          teamsObject?.numberStatus === NumberStatusV2.INVALID_FORMAT
            ? this._translateService.instant('tkey;migrations.number_report.teams_number.validator.invalid_format')
            : teamsObject?.statusMessage,
      },
    ];
    const onBeforeInput = (keyboardEvent: InputEvent, inputValue: string): void => {
      const key = keyboardEvent.data;
      if (key >= '0' && key <= '9') {
        return;
      }

      // Allow '+' characters only at the start of the number
      const targetElement = keyboardEvent.target as HTMLInputElement;
      const firstPlusCharacter = key === '+' && !inputValue.includes('+') && targetElement.selectionStart === 0;
      const replacingEntireValueWithPlus =
        key === '+' && targetElement.selectionStart === 0 && targetElement.selectionEnd === inputValue.length;
      const replacingExistingPlus =
        key === '+' &&
        inputValue.startsWith('+') &&
        targetElement.selectionStart === 0 &&
        targetElement.selectionEnd === 1;

      if (firstPlusCharacter || replacingEntireValueWithPlus || replacingExistingPlus) {
        return;
      }

      keyboardEvent.preventDefault();
    };
    const onPaste = (clipboardEvent: ClipboardEvent): void => {
      clipboardEvent.preventDefault();

      const pastedText = clipboardEvent.clipboardData.getData('text').trim();
      const targetElement = clipboardEvent.target as HTMLInputElement;
      const beforePastedText = targetElement.value.slice(0, targetElement.selectionStart);
      const afterPastedText = targetElement.value.slice(targetElement.selectionEnd, targetElement.value.length);

      let sanitized = beforePastedText + pastedText + afterPastedText;
      sanitized = sanitized[0] + sanitized.slice(1, sanitized.length).replaceAll('+', '');
      sanitized = sanitized.replace(/[^\d+]/g, '').slice(0, maxLength);

      targetElement.value = sanitized;
      targetElement.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const isIgnoredRow = migrationStatus === MigrationStatusV2.IGNORED;

    const baseField: DatatableTextField = {
      type: DatatableFieldTypes.TEXT,
      value: teamsObject?.number ?? '',
      maxLength,
      cellWidth,
      onBlur,
      onBeforeInput,
      onPaste,
      isDisabled: isIgnoredRow,
      validation,
    };

    if (!teamsObject?.number || teamsObject.teamsNumberSource === TeamsNumberSource.NUMBER_FROM_SOURCE) {
      return {
        ...baseField,
        tooltip: isIgnoredRow
          ? this._translateService.instant('tkey;migrations.number_report.ignored.row.disabled.tooltip')
          : '',
      };
    }

    const isTranslatedNumber = teamsObject.teamsNumberSource === TeamsNumberSource.TRANSLATED_NUMBER;
    return {
      ...baseField,
      iconHtmlPrepend: isTranslatedNumber ? this.translationsIcon : this.manualEntryIcon,
      tooltip: isIgnoredRow
        ? this._translateService.instant('tkey;migrations.number_report.ignored.row.disabled.tooltip')
        : isTranslatedNumber
          ? this._buildTranslationTooltip(dn)
          : '',
    };
  }

  private _getDirectoryNumberCellValues(entry: NumberReportEntry<CiscoFields>): Partial<CucmDnReportRow> {
    return {
      migrationStatus: MigrationsService.MIGRATION_STATUS_ROW_MAP.get(entry.migrationStatus).label,
      isMigrationStatusPending: false,
      rowId: entry.rowId,
      directoryNumber: entry.pbxFields.directoryNumber,
      lineType: MigrationsService.LINE_TYPE_ROW_MAP_V2.get(entry.numberInsights.lineUsageType).lineType,
      deviceType: MigrationsService.DEVICE_TYPE_ROW_MAP.get(entry.numberInsights.deviceUsageType).label,
      description: entry.pbxFields.description,
      dnAlertingName: entry.pbxFields.alertingName,
      alertingNameAscii: entry.pbxFields.alertingNameAscii,
      callingSearchSpace: entry.pbxFields.callingSearchSpace,
      routePartition: entry.pbxFields.routePartition,
      totalIncomingCalls: entry.utilization?.incomingCallsCount,
      totalOutgoingCalls: entry.utilization?.outgoingCallsCount,
      totalIncomingMinutes: entry.utilization?.incomingCallsDurationInMinutes,
      totalOutgoingMinutes: entry.utilization?.outgoingCallsDurationInMinutes,
      suggestedCallingProduct: UiSuggestedCallingProduct[entry.numberInsights?.suggestedProduct],
      monthlyPstnIncomingCallsOutgoingMinutes: `${entry.utilization?.monthlyPstnInboundCalls} / ${entry.utilization?.monthlyPstnOutgoingCallsDurationInMinutes}`,
      monthlyPstnInboundCalls: entry.utilization?.monthlyPstnInboundCalls,
      monthlyPstnOutgoingCalls: entry.utilization?.monthlyPstnOutgoingCalls,
      monthlyPstnIncomingCallsDurationInMinutes: entry.utilization?.monthlyPstnIncomingCallsDurationInMinutes,
      monthlyPstnOutgoingCallsDurationInMinutes: entry.utilization?.monthlyPstnOutgoingCallsDurationInMinutes,
    };
  }

  private _getEndUserCellValues(endUser: CiscoNumberReportEndUser): Partial<CucmDnReportRow> {
    return {
      cucmEndUser: endUser?.userId || '',
      displayName: endUser?.displayName || '',
      userStatus: endUser?.ldapUserSyncAttribute || '',
      ldapUserSyncAttribute:
        endUser?.ldapUserSyncAttribute === LdapUserSyncAttribute.LOCAL_USER
          ? '<i class="strong">N/A</i>'
          : endUser?.ldapUserSyncAttribute || '',
      ldapDirectoryName: endUser?.ldapDirectoryName || '',
      firstName: endUser?.firstName || '',
      lastName: endUser?.lastName || '',
      mailId: endUser?.mailId || '',
      ms365User: this._getMs365UserCellValue(endUser?.microsoft365User),
      businessPhoneNumber:
        endUser?.microsoft365User?.businessPhones
          .map((number: string) => this._telephoneNumberFilter.transform(number))
          .join(', ') || '',
      isCellDisabled: {
        ms365User: endUser?.microsoft365User?.lookupStatus === LookupStatus.FOUND,
      },
    };
  }

  private _getMs365UserCellValue(microsoft365User: Microsoft365UserEntry): string {
    if (!microsoft365User) {
      return '';
    }

    switch (microsoft365User.lookupStatus) {
      case LookupStatus.FOUND: {
        return microsoft365User.userPrincipalName;
      }
      case LookupStatus.SKIPPED: {
        return '<i class="strong">N/A</i>';
      }
      case LookupStatus.NOT_FOUND: {
        return 'Not Found';
      }
      default: {
        return '';
      }
    }
  }

  private _initBottomNav(): void {
    const selectedRows = MigrationsService.getSelectedRows(this.data);
    const selectedRowCount = selectedRows.length;
    const allSelectedRowsAreIgnored =
      selectedRowCount > 0 ? MigrationsService.areAllSelectedRowsIgnored(selectedRows) : false;
    const anySelectedRowIsIgnored =
      selectedRowCount > 0 ? MigrationsService.isAnySelectedRowIgnored(selectedRows) : false;

    const bottomNavButtons: BottomNavButton[] = [
      {
        id: 'migrations-cisco-reports-list-back',
        dataAutomation: 'migrations-cisco-dn-report-back-button',
        label: 'tkey;global.button.back.text',
        buttonClass: ButtonStyles.DEFAULT,
        icon: ZiroIcons.BACK_CIRCLE,
        cb: () => this._router.navigate([this._filter ? '../../' : '../'], { relativeTo: this._route }),
      },
      {
        id: this._refreshButtonId,
        dataAutomation: 'migrations-cisco-reports-list-refresh-button',
        label: 'tkey;migrations.cisco.refresh_button.label',
        icon: ZiroIcons.REFRESH,
        buttonClass: ButtonStyles.PRIMARY,
        state: {
          pending: false,
          buttonDisableState: { disabled: false },
        },
        cb: (): void => this.onRefreshClick(),
      },
      {
        id: this._exportButtonId,
        dataAutomation: 'migrations-cisco-dn-report-export-button',
        label: 'tkey;migrations.cisco.directory_numbers_report.xlsx_export.button',
        buttonClass: ButtonStyles.SECONDARY,
        icon: ZiroIcons.EXPORT,
        state: {
          pending: false,
          buttonDisableState: { disabled: false, tooltipKey: '' },
        },
        cb: () => {
          this.onExportDNReportClicked();
        },
      },
      {
        id: this._ignoreUnignoreRowsButtonId,
        dataAutomation: 'migrations-cisco-dn-report-ignore-unignore-rows-button',
        label: !selectedRowCount
          ? 'tkey;migrations.number_report.bottom_nav.ignore_unignore_row.disabled'
          : allSelectedRowsAreIgnored
            ? this._translateService.instant(
                'tkey;migrations.cisco.directory_numbers_report.bottom_nav.unignore_rows',
                { rowNumbers: selectedRowCount },
              )
            : this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.bottom_nav.ignore_rows', {
                rowNumbers: selectedRowCount,
              }),
        buttonClass: ButtonStyles.SECONDARY,
        icon: !selectedRowCount ? undefined : allSelectedRowsAreIgnored ? ZiroIcons.UNIGNORE : ZiroIcons.IGNORE,
        state: {
          pending: false,
          tooltipVisible: !selectedRowCount,
          buttonDisableState: {
            disabled: !selectedRowCount,
            tooltipKey: 'tkey;migrations.number_report.bottom_nav.ignore_unignore_row.disabled.tooltip',
          },
        },
        cb: (): void => this._ignoreSelectedRows(selectedRows, allSelectedRowsAreIgnored),
      },
    ];

    if (this.isAutomationAllowed) {
      bottomNavButtons.push({
        id: this._migrateButtonId,
        dataAutomation: 'migrations-cisco-dn-report-migrate-button',
        label: !selectedRowCount
          ? 'tkey;migrations.cisco.directory_numbers_report.bottom_nav.migrate.disabled'
          : this._translateService.instant('tkey;migrations.cisco.directory_numbers_report.bottom_nav.migrate', {
              count: selectedRowCount,
            }),
        buttonClass: ButtonStyles.PRIMARY,
        icon: ZiroIcons.TEAMS_CALLING_SOLID,
        state: {
          pending: false,
          tooltipVisible: !selectedRowCount || anySelectedRowIsIgnored,
          buttonDisableState: {
            disabled: !selectedRowCount || anySelectedRowIsIgnored,
            tooltipKey: !selectedRowCount
              ? 'tkey;migrations.cisco.directory_numbers_report.bottom_nav.migrate.no_rows_selected_disabled.tooltip'
              : 'tkey;migrations.cisco.directory_numbers_report.bottom_nav.migrate.has_ignored_row_selected.tooltip',
          },
        },
        cb: (): void => this._openMigrateModal(),
      });
    }

    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(bottomNavButtons));
  }

  private _ignoreSelectedRows(selectedRows: CucmDnReportRow[], allSelectedRowsAreIgnored: boolean): void {
    this._migrationsService.dispatchButtonState(this._refreshButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: true,
      },
    });

    this._migrationsService.dispatchButtonState(this._ignoreUnignoreRowsButtonId, {
      pending: true,
      buttonDisableState: {
        disabled: true,
      },
    });

    this._migrationsService.dispatchButtonState(this._exportButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: true,
      },
    });

    this._migrationsService.dispatchButtonState(this._migrateButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: true,
      },
    });

    const currentIgnored: IgnoredEntry[] = this.data
      ?.filter((row) => row.migrationStatus.toUpperCase() === MigrationStatusV2.IGNORED)
      .map((row) => ({
        rowId: row.id,
        ignoreReasonDetails: '',
      }));

    let newIgnoredList: IgnoredEntry[];
    if (allSelectedRowsAreIgnored) {
      newIgnoredList = currentIgnored.filter((ign) => !selectedRows.some((sel) => sel.id === ign.rowId));
    } else {
      const toAdd = selectedRows
        .filter((row) => !currentIgnored.some((ign) => ign.rowId === row.id))
        .map((row) => ({
          rowId: row.id,
          ignoreReasonDetails: '',
        }));
      newIgnoredList = [...currentIgnored, ...toAdd];
    }

    selectedRows.forEach((row) => {
      row.isMigrationStatusPending = true;
    });

    this._migrationsCucmNumberReportContext
      .updateIgnoredDirectoryNumbers(newIgnoredList)
      .pipe(
        take(1),
        finalize(() => this._resetButtonStates()),
      )
      .subscribe({
        next: () => {
          this.data = MigrationsService.deselectAllRows(this.data);
        },
      });
  }

  private _initBreadcrumbs(): void {
    this._breadcrumbsService.updateBreadcrumbs([
      { label: 'tkey;migrations.title', routerLink: true, url: `/migrations/tenants/${this._tenantId}` },
      {
        label: this._translateService.instant('tkey;migrations.number_report.dashboard.breadcrumb', {
          name: this._assessment.name,
        }),
        routerLink: true,
        url: `/migrations/tenants/${this._tenantId}/cisco/${this._assessmentUuid}`,
      },
      { label: 'tkey;migrations.cisco.directory_numbers_report.title' },
    ]);
  }

  private _openMigrateModal(): void {
    const selectedRows = MigrationsService.getSelectedRows(this.data);
    const options: ZiroModalDetailedModalOptions = {
      windowClass: 'dn-report-migrate-modal',
      modalViewProperties: {
        title: 'tkey;migrations.cisco.directory_numbers_report.modal_form.title',
        dialPlans: this._dialPlans,
        bulkDrafts: this._bulkDrafts,
        users: selectedRows,
        tenantId: this._tenantId,
        submitButtonLabel: 'tkey;migrations.cisco.directory_numbers_report.modal_form.action.create.label',
      },
      bodyClass: MigrationsBulkJobMigrationModalComponent,
      size: 'lg',
    };

    this._ziroModalService
      .openDetailedModal(() => options.modalViewProperties, options)
      .pipe(
        filter((response: { draft: MicrosoftBulkJob; isExisting: boolean }) => !!response?.draft),
        switchMap((response) => this._postSave(response)),
        tap(() => (this.data = MigrationsService.deselectAllRows(this.data))),
      )
      .subscribe();
  }

  private _postSave(response: { draft: MicrosoftBulkJob; isExisting: boolean }): Observable<any> {
    return this._microsoftBulkProvisioningPollingService.refresh(this._tenantId).pipe(
      tap(() => this._showSavedDraftToast(response.draft)),
      switchMap(() => this._migrationsCucmNumberReportContext.softRefresh()),
      switchMap(() =>
        this._migrationsBulkJobMigrationModalService.openNavigationModal(
          response.draft,
          response.isExisting,
          this._tenantId,
        ),
      ),
    );
  }

  private _showSavedDraftToast(draft: MicrosoftBulkJob): void {
    const message = `${this._translateService.instant(
      'tkey;automations.zpm.bulk_provisioning.drafts.toast',
    )} - <strong>${draft.bulkJobName}</strong>`;

    this._toastService.push(
      ToastTypes.SUCCESS,
      ZiroIcons.SAVE_SUCCESS,
      'tkey;shared.toast.save.success.title',
      message,
    );
  }

  onTableResultsChanged(): void {
    this._updateFilterCounts();
  }

  private _updateFilterCounts(): void {
    if (!this.datatableComponent?.filteredData) {
      return;
    }

    const activeFilters = this.datatableComponent.filteredData;

    const counts: Record<FilterKeys, Map<string, number>> = {
      migrationStatus: new Map<string, number>(),
      lineType: new Map<string, number>(),
      deviceType: new Map<string, number>(),
      huntListMembership: new Map<string, number>(),
      dnCallingSearchSpace: new Map<string, number>(),
      routePartition: new Map<string, number>(),
      devicePool: new Map<string, number>(),
      deviceCss: new Map<string, number>(),
      suggestedCallingProduct: new Map<string, number>(),
      site: new Map<string, number>(),
    };

    activeFilters.forEach((row) => {
      // Migration Status
      const migrationStatus = row.migrationStatus;
      counts.migrationStatus.set(migrationStatus, (counts.migrationStatus.get(migrationStatus) || 0) + 1);

      // Line Type
      const lineType = row.lineType;
      counts.lineType.set(lineType, (counts.lineType.get(lineType) || 0) + 1);

      // Device Type
      const deviceType = row.deviceType;
      counts.deviceType.set(deviceType, (counts.deviceType.get(deviceType) || 0) + 1);

      // Hunt List Membership
      const huntListMembership = row.huntListMembership;
      huntListMembership.split(',').forEach((hunt: string) => {
        counts.huntListMembership.set(hunt.trim(), (counts.huntListMembership.get(hunt.trim()) || 0) + 1);
      });

      // DN Calling Search Space
      const dnCss = row.callingSearchSpace;
      counts.dnCallingSearchSpace.set(dnCss, (counts.dnCallingSearchSpace.get(dnCss) || 0) + 1);

      // Route Partition
      const routePartition = row.routePartition;
      counts.routePartition.set(routePartition, (counts.routePartition.get(routePartition) || 0) + 1);

      // Device Pool
      const devicePool = row.fields.devicePool.value.value || row.devicePool;
      counts.devicePool.set(devicePool, (counts.devicePool.get(devicePool) || 0) + 1);

      // Device CSS
      const deviceCss = row.fields.associatedDeviceCss.value.value || row.associatedDeviceCss;
      counts.deviceCss.set(deviceCss, (counts.deviceCss.get(deviceCss) || 0) + 1);

      // Suggested Calling Product
      const suggestedProduct = row.suggestedCallingProduct;
      counts.suggestedCallingProduct.set(
        suggestedProduct,
        (counts.suggestedCallingProduct.get(suggestedProduct) || 0) + 1,
      );

      // Site
      const site = row.site;
      counts.site.set(site, (counts.site.get(site) || 0) + 1);
    });

    this.migrationStatusOptionsWithCounts.forEach((opt) => {
      const newCount = counts.migrationStatus.get(opt.value) ?? 0;
      opt.count = newCount;
    });

    this.lineTypeOptionsWithCounts.forEach((opt) => {
      opt.count = counts.lineType.get(opt.value) ?? 0;
    });

    this.deviceTypeOptionsWithCounts.forEach((opt) => {
      opt.count = counts.deviceType.get(opt.value) ?? 0;
    });

    this.countedHuntListMembershipFilterOptions.forEach((opt) => {
      opt.count = counts.huntListMembership.get(opt.value) ?? 0;
    });

    this.dnCallingSearchSpaceOptionsWithCounts.forEach((opt) => {
      opt.count = counts.dnCallingSearchSpace.get(opt.value) ?? 0;
    });

    this.routePartitionOptionsWithCounts.forEach((opt) => {
      opt.count = counts.routePartition.get(opt.value) ?? 0;
    });

    this.devicePoolOptionsWithCounts.forEach((opt) => {
      opt.count = counts.devicePool.get(opt.value) ?? 0;
    });

    this.deviceCssOptionsWithCounts.forEach((opt) => {
      opt.count = counts.deviceCss.get(opt.value) ?? 0;
    });

    this.suggestedCallingProductOptionsWithCounts.forEach((opt) => {
      opt.count = counts.suggestedCallingProduct.get(opt.value) ?? 0;
    });

    this.siteOptionsWithCounts.forEach((opt) => {
      opt.count = counts.site.get(opt.value) ?? 0;
    });

    this._changeDetectorRef.detectChanges();
  }

  private _getSuggestedCallingProductTooltip(entry: NumberReportEntry<CiscoFields>): {
    tooltip: string;
    diffInDays?: number;
  } {
    const usersOldestCdrTimestamp = entry.utilization?.usersOldestCdrTimestamp;
    if (!usersOldestCdrTimestamp) {
      return {
        tooltip: this._translateService.instant(
          'tkey;migrations.cisco.directory_numbers_report.table.suggested_calling_product.no_users_oldest_timestamp.tooltip',
        ),
        diffInDays: null,
      };
    }

    const mostRecentMoment = moment(new Date(this._dnReport.mostRecentCdrTimestamp));
    const usersOldestMoment = moment(new Date(usersOldestCdrTimestamp));
    const diffInDays = mostRecentMoment.diff(usersOldestMoment, 'days');

    const tooltipKey =
      diffInDays < 30
        ? 'tkey;migrations.cisco.directory_numbers_report.table.suggested_calling_product.less_than_30_days.tooltip'
        : 'tkey;migrations.cisco.directory_numbers_report.table.suggested_calling_product.older_than_30_days.tooltip';

    return {
      tooltip: this._translateService.instant(tooltipKey, {
        usersOldestCdrTimestamp: this._getDateFromTimestamp(usersOldestCdrTimestamp),
      }),
      diffInDays,
    };
  }

  private _getDateFromTimestamp(timestamp: string): string {
    return moment(new Date(timestamp)).format('YYYY-MM-DD');
  }

  private _buildLineDisplayMarkup(key: LineUsageTypeV2): string {
    const config = MigrationsService.LINE_TYPE_ROW_MAP_V2.get(key);
    if (!config) {
      return '';
    }

    const lineType = MigrationsService.LINE_TYPE_ROW_MAP_V2.get(key).lineType;

    return `
    <span class="alert alert-inline alert-info">
      <i class="${config.icon} ${config.iconClass || ''}"></i>
      <span>${lineType}</span>
    </span>
  `;
  }

  private _resetButtonStates(): void {
    const selectedRowCount = MigrationsService.getSelectedRows(this.data);

    this._migrationsService.dispatchButtonState(this._refreshButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: false,
      },
    });
    this._migrationsService.dispatchButtonState(this._exportButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: false,
      },
    });
    this._migrationsService.dispatchButtonState(this._migrateButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: !selectedRowCount.length,
        tooltipKey:
          'tkey;migrations.cisco.directory_numbers_report.bottom_nav.migrate.no_rows_selected_disabled.tooltip',
      },
    });
    this._migrationsService.dispatchButtonState(this._ignoreUnignoreRowsButtonId, {
      pending: false,
      buttonDisableState: {
        disabled: !selectedRowCount.length,
        tooltipKey: 'tkey;migrations.number_report.bottom_nav.ignore_unignore_row.disabled',
      },
    });
  }

  private _buildTranslationTooltip(dn: string): string {
    if (!this._normalizedE164TransformationRules) {
      return '';
    }

    const matchingRule = this._normalizedE164TransformationRules.find(
      (rule) => dn.startsWith(rule.fromPrefix) && dn.length === rule.fromPrefix.length + rule.fromXCount,
    );

    if (!matchingRule) {
      return '';
    }

    return this._translateService.instant(
      'tkey;migrations.cisco.directory_numbers_report.table.ms_365_number.tooltip.transformation_rule',
      {
        ruleName: matchingRule.description,
      },
    );
  }

  private _getIconHtml(icon: ZiroIcons): string {
    return `
    <span class="fa-stack">
      <i class="fas ${this.ZiroIcons.CIRCLE} fa-stack-2x"></i>
      <i class="fas ${icon} fa-stack-1x fa-inverse"></i>
    </span>`;
  }

  private _updateDevicePoolCssFilterCounts(
    oldValue: string,
    newValue: string,
    lineDeviceProperty: keyof LineDevice,
  ): void {
    if (lineDeviceProperty !== 'devicePool' && lineDeviceProperty !== 'callingSearchSpace') {
      return;
    }

    if (lineDeviceProperty === 'callingSearchSpace') {
      this.deviceCssOptionsWithCounts.forEach((opt) => {
        if (opt.value === newValue) {
          opt.count += 1;
        }
        if (opt.value === oldValue) {
          opt.count -= 1;
        }
      });
    }

    if (lineDeviceProperty === 'devicePool') {
      this.devicePoolOptionsWithCounts.forEach((opt) => {
        if (opt.value === newValue) {
          opt.count += 1;
        }
        if (opt.value === oldValue) {
          opt.count -= 1;
        }
      });
    }

    this._changeDetectorRef.detectChanges();
  }
}
